import { useState, useEffect } from 'react';
import './ThemeEditorNew.css';
import { getClientTheme, saveClientTheme } from '../../services/api';
import { GOOGLE_FONTS, FONT_CATEGORIES, POPULAR_FONTS, getFontsByCategory, buildGoogleFontsUrl } from '../../utils/fontList';
import ColorPicker from './ColorPicker';
import VoiceWidgetPreview from './VoiceWidgetPreview';
import SidebarWidgetPreview from './SidebarWidgetPreview';

// Extend Window interface for global functions
declare global {
    interface Window {
        refreshWidgetTheme?: () => void;
    }
}

// Utility function to convert RGB/RGBA to HEX (6 characters only, no alpha)
const rgbToHex = (rgb: string): string => {
    if (rgb.startsWith('#')) {
        // If it's already hex, ensure it's only 6 characters (remove alpha if present)
        if (rgb.length === 9) {
            // 8-character hex with alpha, strip the alpha
            return rgb.substring(0, 7);
        }
        return rgb;
    }
    
    // Handle both rgb() and rgba() formats
    const rgbMatch = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbMatch) {
        const r = parseInt(rgbMatch[1]);
        const g = parseInt(rgbMatch[2]);
        const b = parseInt(rgbMatch[3]);
        return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }
    return rgb;
};

// Convert all color properties in theme to HEX
const convertThemeColorsToHex = (theme: any): any => {
    const converted = { ...theme };
    
    // List of color properties that should be converted
    const colorProperties = [
        'primaryColor', 'secondaryColor', 'chatBgColor', 'buttonBgColor', 'buttonTextColor',
        'interactiveBannerBgColor', 'interactiveBannerSecondaryColor', 'interactiveBannerButtonColor', 'interactiveBannerButtonSecondaryColor',
 
        'tainPlaceholderColor', 'tainSendBgColor', 'tainSendIconColor', 'tainInputContainerBgColor', 'tainInputContainerBorderColor',
        // Add chat interface customization colors
        'chatContainerBg', 'chatContainerGradientStart', 'chatContainerGradientEnd', 'chatTextColor',
        'chatCitationTooltipBg', 'chatCitationTooltipText', 'chatSendIconColor', 'chatSendButtonBg',
        'chatCloseIconColor', 'chatCloseIconBg', 'chatMessageResponseBg',
        // Sources tab customization colors
        'chatSourcesTabBorderColor', 'chatSourcesTabTextColor', 'chatSourcesTabActiveTextColor', 'chatSourcesItemBorderColor',
        'chatSourcesItemBg', 'chatSourcesItemHeaderBg', 'chatSourcesLinkColor', 'chatHyperlinkColor',
        'chatSourcesScrollBg', 'chatSourcesScrollBorderColor', 'chatSourcesTextColor',
        'chatSourcesMarkerBg', 'chatSourcesMarkerColor', 'chatSourcesTabMarkerBg', 'chatSourcesTabMarkerColor',
        // Feedback icons colors
        'chatFeedbackIconColor', 'chatFeedbackIconHoverColor', 'chatFeedbackActiveColor',
        'chatFeedbackBg', 'chatFeedbackHoverBg', 'chatFeedbackActiveBg',
        'chatFeedbackBorderColor', 'chatFeedbackHoverBorderColor', 'chatFeedbackActiveBorderColor',
        // Feedback tags colors
        'chatFeedbackTagBg', 'chatFeedbackTagColor', 'chatFeedbackTagBorderColor',
        'chatFeedbackTagHoverBg', 'chatFeedbackTagHoverBorderColor', 'chatFeedbackTagActiveBg',
        'chatFeedbackTagLabelColor', 'chatFeedbackThankYouColor',
        // View Source button colors
        'chatViewSourceBg', 'chatViewSourceColor', 'chatViewSourceHoverBg', 'chatViewSourceHoverColor',
        // Clear button colors
        'clearButtonBg', 'clearButtonText', 'clearButtonHoverBg',
        // Additional UI element colors
        'floatingAvatarBgColor', 'floatingAvatarIconColor', 'floatingAvatarBorderColor',
        'gradientStartColor', 'gradientEndColor', 'borderColor'
    ];
    
    // Also check for widget-specific color properties
    const widgetTypes = ['headerButton', 'miniBanner', 'modernBanner', 'notificationBanner', 'vancouverBanner', 'tainBanner', 'mobileButton'];
    widgetTypes.forEach(widget => {
        colorProperties.push(`${widget}BgColor`, `${widget}TextColor`);
    });
    
    // Convert all color properties to HEX
    colorProperties.forEach(prop => {
        if (converted[prop] && typeof converted[prop] === 'string') {
            converted[prop] = rgbToHex(converted[prop]);
        }
    });
    
    return converted;
};

// Available widget types
const availableWidgets = [
    { key: 'sidebarWidget', name: 'Sidebar Widget (New!)', icon: '⭐', featured: true },
    { key: 'headerButton', name: 'Header Button', icon: '📋' },
    { key: 'mobileButton', name: 'Mobile Button', icon: '📱' },
    { key: 'floatingAvatar', name: 'Floating Avatar', icon: '🤖' },
    { key: 'searchBarTrigger', name: 'Search Bar Trigger', icon: '🛒' },
    { key: 'miniBanner', name: 'Mini Banner', icon: '🏷️' },
    { key: 'modernBanner', name: 'Modern Banner', icon: '🎯' },
    { key: 'notificationBanner', name: 'Notification Banner', icon: '🔔' },
    { key: 'vancouverBanner', name: 'Interactive Banner', icon: '🎨' },
    { key: 'tainBanner', name: 'Card Banner', icon: '💼' },
];

// Available icons
const icons = {
    chat: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" />
        </svg>
    ),
    robot: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 14H21C21 10.13 17.87 7 14 7H13V5.73C13.6 5.39 14 4.74 14 4C14 2.9 13.1 2 12 2C10.9 2 10 2.9 10 4C10 4.74 10.4 5.39 11 5.73V7H10C6.13 7 3 10.13 3 14H2C1.45 14 1 14.45 1 15V18C1 18.55 1.45 19 2 19H3V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V19H22C22.55 19 23 18.55 23 18V15C23 14.45 22.55 14 22 14ZM21 17H19V20H5V17H3V16H5V14C5 11.24 7.24 9 10 9H14C16.76 9 19 11.24 19 14V16H21V17ZM8.5 13.5C7.67 13.5 7 14.17 7 15C7 15.83 7.67 16.5 8.5 16.5C9.33 16.5 10 15.83 10 15C10 14.17 9.33 13.5 8.5 13.5ZM15.5 13.5C14.67 13.5 14 14.17 14 15C14 15.83 14.67 16.5 15.5 16.5C16.33 16.5 17 15.83 17 15C17 14.17 16.33 13.5 15.5 13.5Z" />
        </svg>
    ),
    send: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
    ),
    question: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
        </svg>
    ),
    human: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
    ),
    avatar: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </svg>
    ),
    headset: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 1C7.03 1 3 5.03 3 10v7c0 1.66 1.34 3 3 3h1v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-2v8h1c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9zm-1 14h2v2h-2v-2zm0-6h2v4h-2V9z"/>
        </svg>
    ),
    support: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.89 3 3 3.89 3 5V15C3 16.1 3.9 17 5 17H8L12 21L16 17H19C20.1 17 21 16.1 21 15V5C21 3.89 20.1 3 19 3ZM13 15H11V13H13V15ZM13 11H11V7H13V11Z"/>
        </svg>
    ),
    online: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#10B981"/>
            <circle cx="12" cy="12" r="6" fill="white"/>
            <circle cx="12" cy="12" r="3" fill="#10B981"/>
        </svg>
    ),
    check: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
    ),
};

const ThemeEditor = ({ clientId }: { clientId: string }) => {
    // Check localStorage for saved tab preference
    const savedTab = localStorage.getItem('themeEditorActiveTab') as 'widgetTrigger' | 'chatInterface' | null;
    const [activeTab, setActiveTab] = useState<'widgetTrigger' | 'chatInterface'>(savedTab || 'widgetTrigger');

    // Dark mode state
    const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

    // Detect system dark mode preference
    useEffect(() => {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDarkMode(prefersDark);

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setIsDarkMode(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);
    
    // Create comprehensive default theme
    const createDefaultTheme = () => ({
        // Chat interface variant: 'current' (existing look) or 'standard' (clean, light layout)
        interfaceVariant: 'current',
        // Standard panel position: 'right' | 'left'
        interfacePosition: 'right',
        // Sidebar widget position: 'right' | 'left'
        sidebarPosition: 'right',
        // Enable sidebar widget auto page context detection
        sidebarAutoContext: true,
        // Standard inline source styling defaults
        standardSourceText: '#2563EB',
        standardSourceUnderlineColor: '#2563EB',
        standardSourceIconColor: '#2563EB',
        // Standard panel + header / icon styling
        standardPanelBg: '#EEF2F7',
        // E-commerce configuration
        ecommerceBannerTrigger: {
            enabled: true,
            position: 'bottom-right',
            bgColor: '#007bff',
            iconColor: '#ffffff',
            textColor: '#ffffff',
            pulseAnimation: true,
            size: 'medium',
            shape: 'circle',
            icon: 'microphone',
            tooltipText: 'E-commerce Assistant'
        },
        ecommerceResultsOverlay: {
            bgColor: '#ffffff',
            textColor: '#1f2937',
            borderColor: '#e5e7eb',
            borderRadius: '12px',
            backdropBlur: false,
            maxWidth: '400px',
            maxHeight: '600px',
            animation: 'slide-up'
        },
        // Legacy voiceMode removed - use interfaceVariant='ecommerce' instead
        standardHeaderBg: '#FFFFFF',
        standardBotIconBg: '#E0ECFF',
        standardBotIconColor: '#2563EB',
        standardBotIcon: 'ai-chip', // Default bot icon
        standardBotCustomImage: '', // Custom bot icon image
        standardHideBranding: 'true', // Hide "Powered by brndsh" footer by default for new clients
        // Standard citation links (Grok-style inline)
        standardCitationLinkColor: '#2563EB',
        standardCitationLinkBg: 'rgba(37, 99, 235, 0.08)',
        standardCitationLinkBorder: 'rgba(37, 99, 235, 0.2)',
        standardCitationLinkHoverBg: 'rgba(37, 99, 235, 0.15)',
        // Standard loading state
        standardLoadingIcon: 'spinner',
        standardLoadingText: 'Generating response...',
        // Standard links
        standardLinkColor: '#2563EB',
        standardLinkUnderline: 'true',
        standardLinkUnderlineColor: '', // empty means same as link color
        standardLinkUnderlineThickness: '1px',
        standardLinkFontWeight: 'normal',
        // Standard message bubble defaults
        standardBotBubbleBg: '#FFFFFF',
        standardBotBubbleText: '#111827',
        standardUserBubbleBg: '#2563EB',
        standardUserBubbleText: '#FFFFFF',
        primaryColor: '#667eea',
        secondaryColor: '#2d3748',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        chatBgColor: '#ffffff',
        buttonBgColor: '#667eea',
        buttonTextColor: '#ffffff',
        
        // Chat interface customization
        chatContainerBg: '#374151',
        chatContainerGradientStart: '#1e293b',
        chatContainerGradientEnd: '#334155',
        chatContainerUseGradient: 'true',
        chatContainerOpacity: 95,
        chatTextColor: '#ffffff',
        chatCitationTooltipBg: '#1e293b',
        chatCitationTooltipText: '#ffffff',
        chatSendIconColor: '#ffffff',
        chatSendButtonBg: '#667eea',
        chatCloseIconColor: '#ffffff',
        chatCloseIconBg: '#374151',
        chatMessageResponseBg: '#1e293b',
        
        // Sources tab customization
        chatSourcesTabBorderColor: '#3b82f6',  // Light blue for active tab
        chatSourcesTabTextColor: '#ffffff',     // White text for tabs
        chatSourcesTabActiveTextColor: '#3b82f6', // Active tab text color
        chatSourcesItemBorderColor: '#1e1e1e',  // Dark border
        chatSourcesItemBg: '#1e1e1e',         // Dark background for source items
        chatSourcesItemHeaderBg: '#1e1e1e',   // Dark header background
        chatSourcesLinkColor: '#60a5fa',
        chatHyperlinkColor: '#60a5fa',
        chatSourcesScrollBg: '#1e293b',        // Dark background matching theme
        chatSourcesScrollBorderColor: '#475569',  // Darker border
        chatSourcesTextColor: '#e5e7eb',      // Light text for dark background
        chatSourcesMarkerBg: '#3b82f6',       // Blue background for markers in response
        chatSourcesMarkerColor: '#ffffff',     // White text for markers in response
        chatSourcesTabMarkerBg: '#10b981',    // Green background for sources tab markers
        chatSourcesTabMarkerColor: '#ffffff',  // White text for sources tab markers
        
        // Feedback icons customization
        chatFeedbackIconColor: '#9ca3af',     // Gray default
        chatFeedbackIconHoverColor: '#6b7280', // Darker gray on hover
        chatFeedbackActiveColor: '#3b82f6',    // Blue when active
        chatFeedbackBg: 'transparent',        // Default transparent background
        chatFeedbackHoverBg: 'rgba(107, 114, 128, 0.1)', // Light gray on hover
        chatFeedbackActiveBg: 'rgba(59, 130, 246, 0.1)', // Light blue when active
        chatFeedbackBorderColor: 'transparent', // Default no border
        chatFeedbackHoverBorderColor: '#e5e7eb', // Light gray border on hover
        chatFeedbackActiveBorderColor: '#3b82f6', // Blue border when active
        
        // View Source button customization
        chatViewSourceBg: 'rgba(59, 130, 246, 0.1)',     // Light blue background
        chatViewSourceColor: '#60a5fa',                   // Blue text
        chatViewSourceHoverBg: 'rgba(59, 130, 246, 0.2)', // Darker blue on hover
        chatViewSourceHoverColor: '#93c5fd',               // Lighter blue text on hover
        
        // Clear conversation button
        chatClearButtonShow: 'true',
        chatClearButtonText: 'Clear Chat',
        chatClearButtonBg: '#ef4444',
        chatClearButtonColor: '#ffffff',
        chatClearButtonHoverBg: '#dc2626',
        chatClearButtonHoverColor: '#ffffff',
        chatClearButtonPosition: 'header-right',
        chatClearButtonIcon: 'trash',
        
        // Font size customizations
        chatFontSizeBase: 14,
        chatFontSizeResponse: 14,
        chatFontSizeUser: 14,
        chatFontSizeHeader: 16,
        chatFontSizePlaceholder: 14,
        
        // Link customization
        chatLinkUnderline: 'true',
        chatLinkUnderlineStyle: 'solid',
        chatLinkUnderlineColor: 'currentColor',
        chatLinkUnderlineThickness: '1px',
        chatLinkHoverUnderline: 'true',
        
        // Interactive Banner (vancouverBanner) defaults - Vancouver client design
        interactiveBannerTitleFirst: 'Find',
        interactiveBannerTitleSecond: 'yourself',
        interactiveBannerSubtitle: 'AI-POWERED BRNDSH',
        interactiveBannerPlaceholder: 'Ask me anything about Vancouver!',
        interactiveBannerBgColor: '#f24a91',
        interactiveBannerSecondaryColor: '#ffffff',
        interactiveBannerButtonColor: '#f24a91',
        interactiveBannerButtonSecondaryColor: '#f24a91',
        
        // Card Banner (tainBanner) defaults
        tainPlaceholder: 'Hi! I\'m AI Assistant.\nAsk me anything you need help with',
        tainPlaceholderColor: '#6b7280',
        tainSendBgColor: '#7AA956',
        tainSendIconColor: '#ffffff',
        tainInputContainerPadding: 18,
        tainInputContainerGap: 8,
        tainInputContainerBgColor: '#ffffff',
        tainInputContainerBorderColor: '#E5E7EB',
        
        // Header button
        headerButtonText: '💬 AI Assistant',
        headerButtonIcon: 'robot',
        headerButtonBgColor: '#667eea',
        headerButtonTextColor: '#ffffff',
        headerButtonIconColor: '#ffffff',
        headerButtonBorderRadius: 8,
        headerButtonPadding: 12,
        headerButtonSelector: '',
        
        // Mini banner
        miniBannerText: '💬 Chat with us',
        miniBannerIcon: 'chat',
        miniBannerBgColor: '#667eea',
        miniBannerTextColor: '#ffffff',
        miniBannerBorderRadius: 12,
        miniBannerShadow: true,
        miniBannerSelector: '',
        
        // Modern banner
        modernBannerTitle: '🤖 AI Assistant',
        modernBannerSubtitle: 'Get instant, intelligent answers to your questions',
        modernBannerIcon: 'robot',
        modernBannerBgColor: '#ffffff',
        modernBannerTextColor: '#2d3748',
        modernBannerAccentColor: '#667eea',
        modernBannerBorderRadius: 16,
        modernBannerPadding: 20,
        modernBannerSelector: '',
        
        // Notification banner
        notificationBannerTitle: '🔔 AI Support Available',
        notificationBannerMessage: 'Click here to start a conversation with our AI assistant',
        notificationBannerIcon: 'robot',
        notificationBannerBgColor: '#ffffff',
        notificationBannerTextColor: '#374151',
        notificationBannerIconColor: '#667eea',
        notificationBannerBorderRadius: 12,
        notificationBannerPadding: 16,
        notificationBannerSelector: '',
        
        // Vancouver banner
        vancouverTitleFirst: 'Find',
        vancouverTitleSecond: 'yourself',
        vancouverPlaceholder: 'Ask me anything about Vancouver!',
        vancouverSubtitle: 'AI-POWERED BRNDSH',
        vancouverBgColor: '#f5f5f7',
        vancouverTitleColor: '#f24a91',
        vancouverSubtitleColor: '#333333',
        vancouverSelector: '',
        
        // Mobile button
        mobileButtonText: 'Chat',
        mobileButtonIcon: 'robot',
        mobileButtonBgColor: '#667eea',
        mobileButtonIconColor: '#ffffff',
        mobileButtonShape: 'circle',
        mobileButtonSize: 'medium',
        mobileButtonPosition: 'bottom-right',
        mobileButtonSelector: '',
        
        // Floating Avatar
        floatingAvatarBgColor: '#667eea',
        floatingAvatarIconColor: '#ffffff',
        floatingAvatarShape: 'circle',
        floatingAvatarSize: 'large',
        floatingAvatarCustomWidth: '10',
        floatingAvatarPosition: 'bottom-right',
        floatingAvatarIcon: 'human',
        floatingAvatarPulseAnimation: 'true',
        floatingAvatarBounceAnimation: 'false',
        floatingAvatarBorderColor: '#ffffff',
        floatingAvatarBorderWidth: '3',
        floatingAvatarShadowIntensity: 'medium',
        floatingAvatarHoverEffect: 'scale',
        floatingAvatarTooltipText: '',
        floatingAvatarTooltipPosition: 'left',
        floatingAvatarCustomImage: '',
        floatingAvatarSelector: '',
        
        // Enhanced tooltip/message bubble
        floatingAvatarTooltipShow: 'hover',
        floatingAvatarTooltipDelay: '2',
        floatingAvatarTooltipDuration: '6',
        floatingAvatarTooltipBg: '#ffffff',
        floatingAvatarTooltipTextColor: '#1f2937',
        floatingAvatarTooltipBorderRadius: '18',
        floatingAvatarTooltipPadding: '16',
        floatingAvatarTooltipShadow: 'true',
        floatingAvatarTooltipArrowSize: '10',
        floatingAvatarTooltipMaxWidth: '220',
        floatingAvatarTooltipFontSize: '14',
        floatingAvatarTooltipAnimation: 'fade',
    });

    // Extend the inferred theme type with an index signature so we can use
    // dynamic property access like theme[`${widgetKey}Icon`]
    type Theme = ReturnType<typeof createDefaultTheme> & { [key: string]: any };

    const [theme, setTheme] = useState<Theme>(createDefaultTheme());
    const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
    const [selectedFontCategory, setSelectedFontCategory] = useState<string>('sans-serif');
    const [showFontSelector, setShowFontSelector] = useState<boolean>(false);
    const [previewText, setPreviewText] = useState<string>('The quick brown fox jumps over the lazy dog');
    const [triggerOptions, setTriggerOptions] = useState<Record<string, { placement: 'default' | 'selector'; selector: string }>>({});
    const [tainInputValue, setTainInputValue] = useState<string>('');
    const [saveNotification, setSaveNotification] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({
        show: false,
        type: 'success',
        message: ''
    });
    const [collapsedWidgets, setCollapsedWidgets] = useState<Record<string, boolean>>({});
    const [clientBranding, setClientBranding] = useState<any>(null);
    const [clientPlanTier, setClientPlanTier] = useState<string>('starter');
    const [clientPlan, setClientPlan] = useState<any>(null);

    // Theme Variants State
    const [themeVariants, setThemeVariants] = useState<any[]>([]);
    const [selectedVariant, setSelectedVariant] = useState(0);
    const [showVariantModal, setShowVariantModal] = useState(false);
    const [isLoadingVariants, setIsLoadingVariants] = useState(false);

    // Load client branding data
    useEffect(() => {
        if (!clientId) return;

        const fetchBranding = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/client/${clientId}/profile`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();

                    // Set plan tier and full plan object
                    if (data.plan && data.plan.tier) {
                        setClientPlanTier(data.plan.tier);
                        setClientPlan(data.plan);
                        console.log('Client plan tier:', data.plan.tier);
                        console.log('Client plan features:', data.plan.features);
                    }

                    // The branding data is in the 'branding' field
                    const brandingData = data.branding;
                    if (brandingData && (brandingData.colors || brandingData.fonts)) {
                        setClientBranding(brandingData);
                        console.log('Client branding loaded:', brandingData);
                    } else {
                        console.log('No valid branding data found in response:', data);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch client branding:', error);
            }
        };

        fetchBranding();
    }, [clientId]);


    // Fetch existing theme variants
    const fetchThemeVariants = async () => {
        if (!clientId) return;

        setIsLoadingVariants(true);
        try {
            // Check if e-commerce variant is currently enabled in the theme
            const isEcommerceEnabled = theme.interfaceVariant === 'ecommerce';

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/demo-enhanced/select-theme-variant/${clientId}?ecommerce=${isEcommerceEnabled}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.variants && data.variants.length > 0) {
                    setThemeVariants(data.variants);
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Error fetching theme variants:', error);
            return false;
        } finally {
            setIsLoadingVariants(false);
        }
    };

    // Apply theme variant
    const applyThemeVariant = async (variantIndex: number) => {
        if (!clientId || !themeVariants[variantIndex]) {
            console.error('Cannot apply variant - missing data');
            return;
        }

        setSelectedVariant(variantIndex);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/demo-enhanced/select-theme-variant/${clientId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    variant_index: variantIndex,
                    theme_layout: themeVariants[variantIndex].mapping || themeVariants[variantIndex]
                })
            });

            if (response.ok) {
                const data = await response.json();

                // Update theme with selected variant
                const variantTheme = themeVariants[variantIndex].mapping || themeVariants[variantIndex];
                if (variantTheme) {
                    setTheme(prevTheme => ({
                        ...prevTheme,
                        ...variantTheme
                    }));
                }

                // Trigger theme refresh event
                window.dispatchEvent(new Event('widget-theme-refresh'));

                setSaveNotification({
                    show: true,
                    type: 'success',
                    message: 'Theme variant applied successfully!'
                });
            } else {
                console.error('Failed to apply theme variant');
                setSaveNotification({
                    show: true,
                    type: 'error',
                    message: 'Failed to apply theme variant'
                });
            }
        } catch (error) {
            console.error('Error applying theme variant:', error);
            setSaveNotification({
                show: true,
                type: 'error',
                message: 'Error applying theme variant'
            });
        }
    };

    // Load theme on mount
    useEffect(() => {
        if (!clientId) return;
        
        getClientTheme(clientId)
            .then(data => {
                console.log('Theme data loaded from API:', data);
                if (data) {
                    // Don't merge with defaults - use the data from server as is
                    // The server already has all properties with branding applied
                    const loadedTheme = convertThemeColorsToHex(data);
                    
                    // If triggerOptions exist, populate individual selector properties
                    if (data.triggerOptions) {
                        setTriggerOptions(data.triggerOptions);
                        
                        // Map triggerOptions back to individual selector properties
                        Object.entries(data.triggerOptions).forEach(([triggerKey, options]) => {
                            const selectorProperty = `${triggerKey}Selector`;
                            const triggerOption = options as { placement: 'default' | 'selector'; selector: string };
                            loadedTheme[selectorProperty] = triggerOption.selector || '';
                        });
                    }
                    
                    // Set selected triggers from data
                    if (data.selectedTriggers && data.selectedTriggers.length > 0) {
                        // If standard variant, enforce floating avatar only
                        const effective = (loadedTheme.interfaceVariant || 'current') === 'standard'
                            ? ['floatingAvatar']
                            : data.selectedTriggers;
                        setSelectedTriggers(effective);
                    } else if ((loadedTheme.interfaceVariant || 'current') === 'standard') {
                        // Standard variant without selected triggers defaults to floating avatar
                        setSelectedTriggers(['floatingAvatar']);
                    } else {
                        // For new themes or themes without selectedTriggers, default to floating avatar
                        setSelectedTriggers(['floatingAvatar']);
                    }
                    
                    setTheme(loadedTheme);
                } else {
                    // No theme exists, create default with floating avatar selected
                    console.log('No theme found, using defaults with floating avatar');
                    setTheme(createDefaultTheme());
                    setSelectedTriggers(['floatingAvatar']);
                }
            })
            .catch(err => {
                console.error('Error loading theme:', err);
                // On error, still set defaults with floating avatar
                setTheme(createDefaultTheme());
                setSelectedTriggers(['floatingAvatar']);
            });
    }, [clientId]);

    // No auto-enable logic - branding is visible by default for all plans
    // Users with white label feature can manually check the box to hide it

    // Handle theme changes with color inheritance
    const handleThemeChange = (key: string, value: string | boolean | string[]) => {
        // Convert color values to HEX format, but preserve boolean values
        let processedValue: any = value;
        if (typeof value === 'string') {
            // Handle boolean string conversion
            if (value === 'true') processedValue = true;
            else if (value === 'false') processedValue = false;
            // Handle color values
            else if (key.toLowerCase().includes('color') || key.toLowerCase().includes('bg')) {
                processedValue = rgbToHex(value);
            }
        }

        console.log('Theme change:', key, '=', processedValue);
        setTheme(prev => {
            // Handle nested object updates (e.g., 'voiceMode.enabled')
            if (key.includes('.')) {
                const keys = key.split('.');
                const newTheme = { ...prev };

                // Navigate to the nested object and update the property
                let current = newTheme;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = processedValue;

                return newTheme;
            }

            // Handle top-level property updates
            const newTheme = {
                ...prev,
                [key]: processedValue
            };

            // If any brand color changes, cascade to all widget triggers (unless they've been explicitly customized)
            if (key === 'primaryColor' || key === 'buttonBgColor' || key === 'buttonTextColor') {
                console.log('Brand color changed, cascading to widget triggers...');
                
                // Update main button colors first
                if (key === 'primaryColor') {
                    if (!prev.buttonBgColor || prev.buttonBgColor === prev.primaryColor) {
                        newTheme.buttonBgColor = processedValue;
                    }
                    if (!prev.buttonTextColor) {
                        newTheme.buttonTextColor = '#FFFFFF';
                    }
                }
                
                // Cascade to ALL widget colors that haven't been explicitly customized
                availableWidgets.forEach(widget => {
                    const bgColorKey = `${widget.key}BgColor`;
                    const textColorKey = `${widget.key}TextColor`;
                    const iconColorKey = `${widget.key}IconColor`;
                    
                    // Get the source color to cascade
                    const sourceColor = key === 'primaryColor' ? processedValue : 
                                      key === 'buttonBgColor' ? processedValue : 
                                      newTheme.buttonBgColor || newTheme.primaryColor;
                    
                    // Background colors: cascade if undefined or matches old brand color
                    if (!prev[bgColorKey] || prev[bgColorKey] === prev.primaryColor || prev[bgColorKey] === prev.buttonBgColor) {
                        newTheme[bgColorKey] = sourceColor;
                    }
                    
                    // Text colors: cascade if undefined or white (default contrast)
                    if (!prev[textColorKey] || prev[textColorKey] === '#ffffff' || prev[textColorKey] === '#FFFFFF' || prev[textColorKey] === prev.buttonTextColor) {
                        newTheme[textColorKey] = key === 'buttonTextColor' ? processedValue : newTheme.buttonTextColor || '#FFFFFF';
                    }
                    
                    // Icon colors: cascade if undefined or matches old brand color
                    if (!prev[iconColorKey] || prev[iconColorKey] === prev.primaryColor || prev[iconColorKey] === prev.buttonTextColor) {
                        newTheme[iconColorKey] = key === 'buttonTextColor' ? processedValue : newTheme.buttonTextColor || '#FFFFFF';
                    }
                });
                
                // Special cases for complex widgets
                const brandBgColor = newTheme.buttonBgColor || newTheme.primaryColor;
                const brandTextColor = newTheme.buttonTextColor || '#FFFFFF';
                
                // Interactive Banner (Vancouver)
                if (!prev.interactiveBannerBgColor || prev.interactiveBannerBgColor === prev.primaryColor) {
                    newTheme.interactiveBannerBgColor = brandBgColor;
                }
                if (!prev.interactiveBannerButtonColor || prev.interactiveBannerButtonColor === prev.primaryColor) {
                    newTheme.interactiveBannerButtonColor = brandBgColor;
                }
                if (!prev.vancouverSendIconColor || prev.vancouverSendIconColor === prev.primaryColor) {
                    newTheme.vancouverSendIconColor = brandBgColor;
                }
                
                // Card Banner (Tain)
                if (!prev.tainSendBgColor || prev.tainSendBgColor === prev.primaryColor) {
                    newTheme.tainSendBgColor = brandBgColor;
                }
                if (!prev.tainSendIconColor || prev.tainSendIconColor === prev.buttonTextColor) {
                    newTheme.tainSendIconColor = brandTextColor;
                }
                
                // Modern Banner
                if (!prev.modernBannerLeftBorderColor || prev.modernBannerLeftBorderColor === prev.primaryColor) {
                    newTheme.modernBannerLeftBorderColor = brandBgColor;
                }
                if (!prev.modernBannerIconColor || prev.modernBannerIconColor === prev.primaryColor) {
                    newTheme.modernBannerIconColor = brandBgColor;
                }
                
                // Notification Banner
                if (!prev.notificationBannerIconColor || prev.notificationBannerIconColor === prev.primaryColor) {
                    newTheme.notificationBannerIconColor = brandBgColor;
                }
                if (!prev.notificationBannerDotColor || prev.notificationBannerDotColor === prev.primaryColor) {
                    newTheme.notificationBannerDotColor = brandBgColor;
                }
                
                console.log('Color cascade completed. Updated colors:', {
                    source: `${key}=${processedValue}`,
                    cascaded: Object.keys(newTheme).filter(k => k.includes('Color') && newTheme[k] !== prev[k])
                });
            }

            return newTheme;
        });

        // Trigger immediate widget refresh for certain properties
        if (['floatingAvatarTooltipShow', 'floatingAvatarTooltipDelay', 'floatingAvatarTooltipDuration', 'floatingAvatarTooltipPosition', 'floatingAvatarTooltipBg', 'floatingAvatarTooltipTextColor'].includes(key)) {
            setTimeout(() => {
                if (window.refreshWidgetTheme) {
                    console.log('Triggering immediate widget refresh for floating avatar change...');
                    window.refreshWidgetTheme();
                }
            }, 100);
        }
    };

    // Font loading function
    const loadFont = (fontName: string) => {
        // Remove old font links
        const oldLinks = document.querySelectorAll('link[data-theme-font]');
        oldLinks.forEach(link => link.remove());

        // Add new font link
        if (fontName && !['Arial', 'Helvetica', 'Times New Roman', 'Georgia'].includes(fontName)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = buildGoogleFontsUrl(fontName);
            link.setAttribute('data-theme-font', 'true');
            document.head.appendChild(link);
        }
    };

    // Load font when fontFamily changes
    useEffect(() => {
        if (theme.fontFamily) {
            const fontName = theme.fontFamily.split(',')[0].replace(/['"]/g, '').trim();
            loadFont(fontName);
        }
    }, [theme.fontFamily]);

    // Toggle widget selection
    const toggleWidget = (widgetKey: string) => {
        // When using Standard interface, only Floating Avatar trigger is allowed
        if ((theme.interfaceVariant || 'current') === 'standard' && widgetKey !== 'floatingAvatar') {
            return;
        }
        setSelectedTriggers(prev => 
            prev.includes(widgetKey)
                ? prev.filter(t => t !== widgetKey)
                : [...prev, widgetKey]
        );
    };

    const toggleWidgetCollapse = (widgetKey: string) => {
        setCollapsedWidgets(prev => ({
            ...prev,
            [widgetKey]: !prev[widgetKey]
        }));
    };

    // Save theme
    const saveTheme = async () => {
        try {
            // Map individual selector properties to triggerOptions structure
            const mappedTriggerOptions: Record<string, { placement: 'default' | 'selector'; selector: string }> = {};
            
            // Map each selected trigger's selector property to triggerOptions
            selectedTriggers.forEach(triggerKey => {
                const selectorProperty = `${triggerKey}Selector`;
                const selectorValue = theme[selectorProperty] || '';
                
                mappedTriggerOptions[triggerKey] = {
                    placement: selectorValue.trim() ? 'selector' : 'default',
                    selector: selectorValue.trim()
                };
            });
            
            // For standard variant, force only Floating Avatar trigger
            const enforcedTriggers = (theme.interfaceVariant || 'current') === 'standard'
                ? ['floatingAvatar']
                : selectedTriggers;
            
            const themeData = {
                ...theme,
                selectedTriggers: enforcedTriggers,
                triggerOptions: Object.fromEntries(
                    Object.entries(mappedTriggerOptions).filter(([k]) => enforcedTriggers.includes(k))
                )
            };
            console.log('Saving theme data with mapped triggerOptions:', themeData);
            console.log('Floating avatar settings:', {
                size: themeData.floatingAvatarSize,
                customWidth: themeData.floatingAvatarCustomWidth,
                icon: themeData.floatingAvatarIcon,
                customImage: themeData.floatingAvatarCustomImage
            });
            
            await saveClientTheme(clientId, themeData);
            
            // Trigger widget refresh
            if (window.refreshWidgetTheme) {
                console.log('Triggering widget theme refresh...');
                window.refreshWidgetTheme();
            } else {
                // Fallback: dispatch custom event
                window.dispatchEvent(new Event('widget-theme-refresh'));
            }
            
            setSaveNotification({
                show: true,
                type: 'success',
                message: 'Theme saved successfully!'
            });
            
            setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
        } catch (error) {
            console.error('Error saving theme:', error);
            setSaveNotification({
                show: true,
                type: 'error',
                message: 'Failed to save theme. Please try again.'
            });
            
            setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
        }
    };

    // Reset to defaults
    const resetToDefaults = () => {
        setTheme(convertThemeColorsToHex(createDefaultTheme()));
        setSelectedTriggers([]);
        setTriggerOptions({});
    };


    // Render selected widget block with inline customization and preview
    const renderSelectedWidget = (widgetKey: string) => {
        const widget = availableWidgets.find(w => w.key === widgetKey);
        if (!widget) return null;
        const isCollapsed = collapsedWidgets[widgetKey];
        
        return (
            <div key={widgetKey} className="selected-widget-block" style={{background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 24, overflow: 'hidden'}}>
                {/* Header spans across both panels */}
                <div 
                    className="widget-block-header" 
                    onClick={() => toggleWidgetCollapse(widgetKey)}
                    style={{
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        padding: '16px 20px', 
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: isCollapsed ? '#f9fafb' : 'white',
                        transition: 'background 0.2s'
                    }}
                >
                    <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <span style={{fontSize: 20}}>{widget.icon}</span>
                        <span style={{fontWeight: 600, fontSize: 16, color: '#1f2937'}}>{widget.name}</span>
                        <span style={{fontSize: 14, color: '#6b7280'}}>
                            {isCollapsed ? '▸' : '▾'}
                        </span>
                    </div>
                    {!((widgetKey === 'searchBarTrigger' && theme.interfaceVariant === 'ecommerce') || 
                       (widgetKey === 'floatingAvatar' && theme.interfaceVariant === 'standard')) && (
                        <button
                            className="remove-widget"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleWidget(widgetKey);
                            }}
                            title="Remove widget"
                            style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#dc2626', padding: '4px'}}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Content grid - only show when not collapsed */}
                {!isCollapsed && (
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 320px', gap: 0}}>
                        {/* Left: Customization Controls */}
                        <div className="widget-customization" style={{padding: 20}}>
                            {/* Inline Grid Layout for Quick Access */}
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                
                                {/* Text Content */}
                                {(widgetKey === 'headerButton' || widgetKey === 'miniBanner' || widgetKey === 'modernBanner' || widgetKey === 'notificationBanner') && (
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            📝 {widgetKey === 'headerButton' ? 'Button Text' : widgetKey === 'miniBanner' ? 'Banner Text' : widgetKey === 'modernBanner' ? 'Title' : 'Title'}
                                        </label>
                                        <input
                                            type="text"
                                            value={theme[`${widgetKey}${widgetKey === 'headerButton' ? 'Text' : widgetKey === 'miniBanner' ? 'Text' : 'Title'}`] || ''}
                                            onChange={(e) => handleThemeChange(`${widgetKey}${widgetKey === 'headerButton' ? 'Text' : widgetKey === 'miniBanner' ? 'Text' : 'Title'}`, e.target.value)}
                                            style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                        />
                                    </div>
                                )}

                                {/* Interactive Banner Text Controls */}
                                {widgetKey === 'vancouverBanner' && (
                                    <>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📝 Title First Part
                                            </label>
                                            <input
                                                type="text"
                                                value={theme.interactiveBannerTitleFirst || ''}
                                                onChange={(e) => handleThemeChange('interactiveBannerTitleFirst', e.target.value)}
                                                placeholder="Get"
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📝 Title Second Part
                                            </label>
                                            <input
                                                type="text"
                                                value={theme.interactiveBannerTitleSecond || ''}
                                                onChange={(e) => handleThemeChange('interactiveBannerTitleSecond', e.target.value)}
                                                placeholder="instant answers"
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📝 Subtitle
                                            </label>
                                            <input
                                                type="text"
                                                value={theme.interactiveBannerSubtitle || ''}
                                                onChange={(e) => handleThemeChange('interactiveBannerSubtitle', e.target.value)}
                                                placeholder="Powered by AI"
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📝 Input Placeholder
                                            </label>
                                            <input
                                                type="text"
                                                value={theme.interactiveBannerPlaceholder || ''}
                                                onChange={(e) => handleThemeChange('interactiveBannerPlaceholder', e.target.value)}
                                                placeholder="Ask me anything..."
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            />
                                        </div>
                                        
                                        {/* Background Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Background Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerBgColor || '#f24a91'}
                                                onChange={(value) => handleThemeChange('interactiveBannerBgColor', value)}
                                            />
                                        </div>

                                        {/* Secondary Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Secondary Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerSecondaryColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('interactiveBannerSecondaryColor', value)}
                                            />
                                        </div>

                                        {/* Button Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Button Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerButtonColor || '#f24a91'}
                                                onChange={(value) => handleThemeChange('interactiveBannerButtonColor', value)}
                                            />
                                        </div>

                                        {/* Button Secondary Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Button Secondary Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerButtonSecondaryColor || '#f24a91'}
                                                onChange={(value) => handleThemeChange('interactiveBannerButtonSecondaryColor', value)}
                                            />
                                        </div>

                                        {/* Input Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🖼️ Input Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerBorderColor || 'rgba(0, 0, 0, 0.1)'}
                                                onChange={(value) => handleThemeChange('interactiveBannerBorderColor', value)}
                                            />
                                        </div>

                                        {/* Input Focus Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🖼️ Focus Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerFocusBorderColor || theme.interactiveBannerButtonColor || '#f24a91'}
                                                onChange={(value) => handleThemeChange('interactiveBannerFocusBorderColor', value)}
                                            />
                                        </div>

                                        {/* Box Shadow Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💫 Box Shadow Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerShadowColor || 'rgba(0, 0, 0, 0.05)'}
                                                onChange={(value) => handleThemeChange('interactiveBannerShadowColor', value)}
                                            />
                                        </div>

                                        {/* Box Shadow Intensity */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💫 Shadow Intensity
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.1"
                                                    value={theme.interactiveBannerShadowOpacity || 0.05}
                                                    onChange={(e) => handleThemeChange('interactiveBannerShadowOpacity', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{Math.round((theme.interactiveBannerShadowOpacity || 0.05) * 100)}%</span>
                                            </div>
                                        </div>

                                        {/* Container Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔲 Container Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerContainerBorderColor || 'transparent'}
                                                onChange={(value) => handleThemeChange('interactiveBannerContainerBorderColor', value)}
                                            />
                                        </div>

                                        {/* Container Border Width */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔲 Container Border Width
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="10"
                                                    value={theme.interactiveBannerContainerBorderWidth || 0}
                                                    onChange={(e) => handleThemeChange('interactiveBannerContainerBorderWidth', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.interactiveBannerContainerBorderWidth || 0}px</span>
                                            </div>
                                        </div>

                                        {/* Container Border Radius */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔲 Container Border Radius
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="50"
                                                    value={theme.interactiveBannerContainerBorderRadius || 0}
                                                    onChange={(e) => handleThemeChange('interactiveBannerContainerBorderRadius', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.interactiveBannerContainerBorderRadius || 0}px</span>
                                            </div>
                                        </div>

                                        {/* Container Box Shadow Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🌟 Container Shadow Color
                                            </label>
                                            <ColorPicker
                                                value={theme.interactiveBannerContainerShadowColor || 'rgba(0, 0, 0, 0.1)'}
                                                onChange={(value) => handleThemeChange('interactiveBannerContainerShadowColor', value)}
                                            />
                                        </div>

                                        {/* Container Shadow Intensity */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🌟 Container Shadow Intensity
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.1"
                                                    value={theme.interactiveBannerContainerShadowOpacity || 0}
                                                    onChange={(e) => handleThemeChange('interactiveBannerContainerShadowOpacity', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{Math.round((theme.interactiveBannerContainerShadowOpacity || 0) * 100)}%</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Card Banner Text Controls */}
                                {widgetKey === 'tainBanner' && (
                                    <>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💬 Placeholder Text
                                            </label>
                                            <textarea
                                                value={theme.tainPlaceholder || ''}
                                                onChange={(e) => handleThemeChange('tainPlaceholder', e.target.value)}
                                                placeholder="Hi! I'm AI Assistant.&#10;Ask me anything you need help with"
                                                rows={3}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical'}}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Icon Selection */}
                                {(widgetKey === 'headerButton' || widgetKey === 'miniBanner' || widgetKey === 'modernBanner' || widgetKey === 'notificationBanner') && (
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            🔸 Icon
                                        </label>
                                        <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                                            {Object.entries(icons).map(([key, icon]) => (
                                                <button
                                                    key={key}
                                                    onClick={() => handleThemeChange(`${widgetKey}Icon`, key)}
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        border: theme[`${widgetKey}Icon`] === key ? '2px solid #6366f1' : '1px solid #d1d5db',
                                                        borderRadius: 6,
                                                        background: theme[`${widgetKey}Icon`] === key ? 'rgba(99, 102, 241, 0.1)' : 'white',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: theme[`${widgetKey}Icon`] === key ? '#6366f1' : '#6b7280',
                                                        fontSize: 14,
                                                        transition: 'all 0.2s'
                                                    }}
                                                    title={key}
                                                >
                                                    {icon}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Modern Banner Specific Controls */}
                                {widgetKey === 'modernBanner' && (
                                    <>
                                        {/* Left Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🖼️ Left Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.modernBannerLeftBorderColor || theme.primaryColor || '#667eea'}
                                                onChange={(value) => handleThemeChange('modernBannerLeftBorderColor', value)}
                                            />
                                        </div>

                                        {/* Left Border Width */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🖼️ Left Border Width
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="10"
                                                    value={theme.modernBannerLeftBorderWidth || 4}
                                                    onChange={(e) => handleThemeChange('modernBannerLeftBorderWidth', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.modernBannerLeftBorderWidth || 4}px</span>
                                            </div>
                                        </div>

                                        {/* Icon Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔸 Icon Color
                                            </label>
                                            <ColorPicker
                                                value={theme.modernBannerIconColor || theme.primaryColor || '#667eea'}
                                                onChange={(value) => handleThemeChange('modernBannerIconColor', value)}
                                            />
                                        </div>

                                        {/* Container Box Shadow */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💫 Box Shadow Color
                                            </label>
                                            <ColorPicker
                                                value={theme.modernBannerShadowColor || 'rgba(0, 0, 0, 0.1)'}
                                                onChange={(value) => handleThemeChange('modernBannerShadowColor', value)}
                                            />
                                        </div>

                                        {/* Shadow Intensity */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💫 Shadow Intensity
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.1"
                                                    value={theme.modernBannerShadowOpacity || 0}
                                                    onChange={(e) => handleThemeChange('modernBannerShadowOpacity', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{Math.round((theme.modernBannerShadowOpacity || 0) * 100)}%</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Background Color - Skip for tainBanner */}
                                {widgetKey !== 'tainBanner' && (
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            🎨 Background
                                        </label>
                                        <ColorPicker
                                            value={theme[`${widgetKey}BgColor`] || theme.buttonBgColor || theme.primaryColor}
                                            onChange={(value) => handleThemeChange(`${widgetKey}BgColor`, value)}
                                        />
                                    </div>
                                )}

                                {/* Text Color */}
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        ✏️ Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme[`${widgetKey}TextColor`] || theme.buttonTextColor}
                                        onChange={(value) => handleThemeChange(`${widgetKey}TextColor`, value)}
                                    />
                                </div>

                                {/* Icon Color - only for widgets with icons */}
                                {(widgetKey === 'headerButton' || widgetKey === 'miniBanner' || widgetKey === 'modernBanner' || widgetKey === 'notificationBanner') && (
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            🎨 Icon Color
                                        </label>
                                        <ColorPicker
                                            value={theme[`${widgetKey}IconColor`] || theme[`${widgetKey}TextColor`] || theme.buttonTextColor}
                                            onChange={(value) => handleThemeChange(`${widgetKey}IconColor`, value)}
                                        />
                                    </div>
                                )}

                                {/* Border Radius */}
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        📐 Border Radius
                                    </label>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            value={theme[`${widgetKey}BorderRadius`] || 8}
                                            onChange={(e) => handleThemeChange(`${widgetKey}BorderRadius`, e.target.value)}
                                            style={{flex: 1}}
                                        />
                                        <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme[`${widgetKey}BorderRadius`] || 8}px</span>
                                    </div>
                                </div>

                                {/* CSS Selector */}
                                <div className="control-group" style={{gridColumn: 'span 2'}}>
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        📍 CSS Selector (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={theme[`${widgetKey}Selector`] || ''}
                                        onChange={(e) => handleThemeChange(`${widgetKey}Selector`, e.target.value)}
                                        placeholder={`#${widgetKey}-container`}
                                        style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'monospace'}}
                                    />
                                </div>

                                {/* Floating Avatar Specific Controls */}
                                {widgetKey === 'floatingAvatar' && (
                                    <>
                                        {/* Shape Selection */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔷 Shape
                                            </label>
                                            <select
                                                value={theme.floatingAvatarShape || 'circle'}
                                                onChange={(e) => handleThemeChange('floatingAvatarShape', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="circle">Circle</option>
                                                <option value="square">Square</option>
                                                <option value="rounded">Rounded</option>
                                                <option value="hexagon">Hexagon</option>
                                            </select>
                                        </div>

                                        {/* Size Selection */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📏 Size
                                            </label>
                                            <select
                                                value={theme.floatingAvatarSize || 'large'}
                                                onChange={(e) => handleThemeChange('floatingAvatarSize', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="small">Small (40px)</option>
                                                <option value="medium">Medium (56px)</option>
                                                <option value="large">Large (64px)</option>
                                                <option value="xlarge">Extra Large (80px)</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                        </div>

                                        {/* Custom Width Slider - Only show when custom size is selected */}
                                        {theme.floatingAvatarSize === 'custom' && (
                                            <div className="control-group">
                                                <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                    📐 Custom Width
                                                </label>
                                                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                    <input
                                                        type="range"
                                                        min="5"
                                                        max="30"
                                                        step="0.5"
                                                        value={parseFloat(theme.floatingAvatarCustomWidth || '10')}
                                                        onChange={(e) => handleThemeChange('floatingAvatarCustomWidth', e.target.value)}
                                                        style={{flex: 1}}
                                                    />
                                                    <span style={{minWidth: 45, fontSize: 12, color: '#6b7280'}}>
                                                        {theme.floatingAvatarCustomWidth || '10'}%
                                                    </span>
                                                </div>
                                                <div style={{fontSize: 11, color: '#6b7280', marginTop: 4}}>
                                                    Width as percentage of viewport width
                                                </div>
                                            </div>
                                        )}

                                        {/* Position Selection */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📍 Position
                                            </label>
                                            <select
                                                value={theme.floatingAvatarPosition || 'bottom-right'}
                                                onChange={(e) => handleThemeChange('floatingAvatarPosition', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="bottom-right">Bottom Right</option>
                                                <option value="bottom-left">Bottom Left</option>
                                                <option value="top-right">Top Right</option>
                                                <option value="top-left">Top Left</option>
                                                <option value="middle-right">Middle Right</option>
                                                <option value="middle-left">Middle Left</option>
                                            </select>
                                        </div>

                                        {/* Icon Selection */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                👤 Icon
                                            </label>
                                            <select
                                                value={theme.floatingAvatarIcon || 'human'}
                                                onChange={(e) => handleThemeChange('floatingAvatarIcon', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="human">Human</option>
                                                <option value="avatar">Avatar</option>
                                                <option value="headset">Headset</option>
                                                <option value="robot">Robot</option>
                                                <option value="chat">Chat</option>
                                                <option value="support">Support</option>
                                                <option value="question">Question</option>
                                                <option value="custom">Custom Image</option>
                                            </select>
                                        </div>

                                        {/* Custom Image URL and Upload */}
                                        {theme.floatingAvatarIcon === 'custom' && (
                                            <div className="control-group">
                                                <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                    🖼️ Custom Image
                                                </label>
                                                <div style={{
                                                    background: '#f3f4f6',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    padding: '8px 12px',
                                                    marginBottom: 12,
                                                    fontSize: 11,
                                                    color: '#4b5563'
                                                }}>
                                                    <strong>💡 Tip:</strong> Use a square image (128x128 pixels) for best results. The image will be automatically resized to fit the avatar dimensions.
                                                </div>
                                                
                                                {/* URL Input */}
                                                <div style={{marginBottom: 8}}>
                                                    <label style={{fontSize: 11, color: '#6b7280', marginBottom: 4, display: 'block'}}>
                                                        Image URL
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={theme.floatingAvatarCustomImage || ''}
                                                        onChange={(e) => handleThemeChange('floatingAvatarCustomImage', e.target.value)}
                                                        placeholder="https://example.com/avatar.png"
                                                        style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                                    />
                                                </div>
                                                
                                                {/* File Upload */}
                                                <div>
                                                    <label style={{fontSize: 11, color: '#6b7280', marginBottom: 4, display: 'block'}}>
                                                        Or upload image (SVG, PNG, JPG, GIF)
                                                    </label>
                                                    <div style={{fontSize: 10, color: '#9ca3af', marginBottom: 6}}>
                                                        Recommended size: 128x128 pixels for best quality
                                                    </div>
                                                    <input
                                                        type="file"
                                                        accept="image/*,.svg"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                // Convert to base64 data URL
                                                                const reader = new FileReader();
                                                                reader.onload = (event) => {
                                                                    const dataUrl = event.target?.result as string;
                                                                    handleThemeChange('floatingAvatarCustomImage', dataUrl);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                        style={{
                                                            width: '100%', 
                                                            padding: '6px 10px', 
                                                            border: '1px solid #d1d5db', 
                                                            borderRadius: 6, 
                                                            fontSize: 13,
                                                            backgroundColor: '#f9fafb'
                                                        }}
                                                    />
                                                </div>
                                                
                                                {/* Preview if image is set */}
                                                {theme.floatingAvatarCustomImage && (
                                                    <div style={{marginTop: 8, textAlign: 'center'}}>
                                                        <img 
                                                            src={theme.floatingAvatarCustomImage} 
                                                            alt="Custom avatar preview"
                                                            style={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: theme.floatingAvatarShape === 'circle' ? '50%' : 
                                                                               theme.floatingAvatarShape === 'rounded' ? '8px' : '0',
                                                                border: '1px solid #e5e7eb',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).style.display = 'none';
                                                            }}
                                                        />
                                                        <div style={{fontSize: 10, color: '#6b7280', marginTop: 4}}>
                                                            Preview
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Border Width */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔲 Border Width
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="10"
                                                    value={theme.floatingAvatarBorderWidth || 3}
                                                    onChange={(e) => handleThemeChange('floatingAvatarBorderWidth', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.floatingAvatarBorderWidth || 3}px</span>
                                            </div>
                                        </div>

                                        {/* Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.floatingAvatarBorderColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('floatingAvatarBorderColor', value)}
                                            />
                                        </div>

                                        {/* Shadow Intensity */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🌑 Shadow Intensity
                                            </label>
                                            <select
                                                value={theme.floatingAvatarShadowIntensity || 'medium'}
                                                onChange={(e) => handleThemeChange('floatingAvatarShadowIntensity', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="none">None</option>
                                                <option value="light">Light</option>
                                                <option value="medium">Medium</option>
                                                <option value="heavy">Heavy</option>
                                            </select>
                                        </div>

                                        {/* Hover Effect */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                ✨ Hover Effect
                                            </label>
                                            <select
                                                value={theme.floatingAvatarHoverEffect || 'scale'}
                                                onChange={(e) => handleThemeChange('floatingAvatarHoverEffect', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="none">None</option>
                                                <option value="scale">Scale</option>
                                                <option value="lift">Lift</option>
                                                <option value="glow">Glow</option>
                                                <option value="rotate">Rotate</option>
                                            </select>
                                        </div>

                                        {/* Pulse Animation */}
                                        <div className="control-group">
                                            <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                                <input
                                                    type="checkbox"
                                                    checked={theme.floatingAvatarPulseAnimation === 'true'}
                                                    onChange={(e) => handleThemeChange('floatingAvatarPulseAnimation', String(e.target.checked))}
                                                    style={{width: 16, height: 16}}
                                                />
                                                <span style={{fontSize: 13, color: '#374151'}}>💫 Pulse Animation</span>
                                            </label>
                                        </div>

                                        {/* Bounce Animation */}
                                        <div className="control-group">
                                            <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                                <input
                                                    type="checkbox"
                                                    checked={theme.floatingAvatarBounceAnimation === 'true'}
                                                    onChange={(e) => handleThemeChange('floatingAvatarBounceAnimation', String(e.target.checked))}
                                                    style={{width: 16, height: 16}}
                                                />
                                                <span style={{fontSize: 13, color: '#374151'}}>🏀 Bounce Animation</span>
                                            </label>
                                        </div>

                                        {/* Message Bubble Section */}
                                        <div className="control-group" style={{gridColumn: 'span 2'}}>
                                            <h5 style={{fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12, borderTop: '1px solid #e5e7eb', paddingTop: 16}}>
                                                💬 Message Bubble Customization
                                            </h5>
                                        </div>

                                        {/* Message Text */}
                                        <div className="control-group" style={{gridColumn: 'span 2'}}>
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Message Text
                                            </label>
                                            <textarea
                                                value={theme.floatingAvatarTooltipText || ''}
                                                onChange={(e) => handleThemeChange('floatingAvatarTooltipText', e.target.value)}
                                                placeholder="Hi! How can I help you today?"
                                                rows={3}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical'}}
                                            />
                                            <p style={{fontSize: 11, color: '#6b7280', marginTop: 4}}>
                                                Supports multiple lines - press Enter for new line
                                            </p>
                                        </div>

                                        {/* Display Mode */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Display Mode
                                            </label>
                                            <select
                                                value={theme.floatingAvatarTooltipShow || 'hover'}
                                                onChange={(e) => handleThemeChange('floatingAvatarTooltipShow', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="hover">Show on hover (desktop only)</option>
                                                <option value="always">Always visible</option>
                                                <option value="never">Never show</option>
                                                <option value="delay">Show after delay, then stay</option>
                                                <option value="auto_hide">Show briefly, then hide</option>
                                            </select>
                                            <p style={{fontSize: 11, color: '#6b7280', marginTop: 4}}>
                                                Phones have no hover, so "Show on hover" never appears on mobile.
                                                Use "Show briefly, then hide" to greet mobile visitors without the
                                                bubble taking up the screen for the whole visit.
                                            </p>
                                        </div>

                                        {/* Timing, only relevant to the two self-revealing modes */}
                                        {(theme.floatingAvatarTooltipShow === 'delay' || theme.floatingAvatarTooltipShow === 'auto_hide') && (
                                            <div className="control-group">
                                                <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                    Wait before showing (seconds)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    value={theme.floatingAvatarTooltipDelay ?? '2'}
                                                    onChange={(e) => handleThemeChange('floatingAvatarTooltipDelay', e.target.value)}
                                                    style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                                />
                                            </div>
                                        )}

                                        {theme.floatingAvatarTooltipShow === 'auto_hide' && (
                                            <div className="control-group">
                                                <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                    Stay visible for (seconds)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={theme.floatingAvatarTooltipDuration ?? '6'}
                                                    onChange={(e) => handleThemeChange('floatingAvatarTooltipDuration', e.target.value)}
                                                    style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                                />
                                                <p style={{fontSize: 11, color: '#6b7280', marginTop: 4}}>
                                                    Set to 0 to leave it up once it appears.
                                                </p>
                                            </div>
                                        )}

                                        {/* Position */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Bubble Position
                                            </label>
                                            <select
                                                value={theme.floatingAvatarTooltipPosition || 'left'}
                                                onChange={(e) => handleThemeChange('floatingAvatarTooltipPosition', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="left">Left side</option>
                                                <option value="right">Right side</option>
                                            </select>
                                        </div>

                                        {/* Background Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Background Color
                                            </label>
                                            <ColorPicker
                                                value={theme.floatingAvatarTooltipBg || '#1f2937'}
                                                onChange={(value) => handleThemeChange('floatingAvatarTooltipBg', value)}
                                            />
                                        </div>

                                        {/* Text Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Text Color
                                            </label>
                                            <ColorPicker
                                                value={theme.floatingAvatarTooltipTextColor || '#1f2937'}
                                                onChange={(value) => handleThemeChange('floatingAvatarTooltipTextColor', value)}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Search Bar Trigger Specific Controls */}
                                {widgetKey === 'searchBarTrigger' && (
                                    <>
                                        {/* Position Selection */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📍 Position
                                            </label>
                                            <select
                                                value={theme.ecommerceBannerTrigger?.position || 'bottom-left'}
                                                onChange={(e) => handleThemeChange('ecommerceBannerTrigger.position', e.target.value)}
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            >
                                                <option value="bottom-left">Bottom Left</option>
                                                <option value="bottom-right">Bottom Right</option>
                                                <option value="bottom-center">Bottom Center</option>
                                                <option value="top-left">Top Left</option>
                                                <option value="top-right">Top Right</option>
                                                <option value="top-center">Top Center</option>
                                            </select>
                                        </div>

                                        {/* Background Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Background Color
                                            </label>
                                            <ColorPicker
                                                value={theme.ecommerceBannerTrigger?.bgColor || theme.primaryColor || '#007bff'}
                                                onChange={(value) => handleThemeChange('ecommerceBannerTrigger.bgColor', value)}
                                            />
                                        </div>

                                        {/* Icon Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Icon & Text Color
                                            </label>
                                            <ColorPicker
                                                value={theme.ecommerceBannerTrigger?.iconColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('ecommerceBannerTrigger.iconColor', value)}
                                            />
                                        </div>

                                        {/* Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Border Color
                                            </label>
                                            <ColorPicker
                                                value={(theme.ecommerceBannerTrigger as any)?.borderColor || 'transparent'}
                                                onChange={(value) => handleThemeChange('ecommerceBannerTrigger.borderColor', value)}
                                            />
                                        </div>

                                        {/* Border Radius */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🔷 Border Radius
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="8"
                                                    max="50"
                                                    value={parseInt((theme.ecommerceBannerTrigger as any)?.borderRadius || '28')}
                                                    onChange={(e) => handleThemeChange('ecommerceBannerTrigger.borderRadius', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{(theme.ecommerceBannerTrigger as any)?.borderRadius || '28'}px</span>
                                            </div>
                                        </div>

                                        {/* Input Placeholder */}
                                        <div className="control-group" style={{gridColumn: 'span 2'}}>
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💬 Input Placeholder
                                            </label>
                                            <input
                                                type="text"
                                                value={(theme.ecommerceBannerTrigger as any)?.placeholder || 'Search products...'}
                                                onChange={(e) => handleThemeChange('ecommerceBannerTrigger.placeholder', e.target.value)}
                                                placeholder="Search products..."
                                                style={{width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13}}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* TAIN Banner Specific Controls */}
                                {widgetKey === 'tainBanner' && (
                                    <>


                                        {/* Send Button Background */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                ➤ Send Button Background
                                            </label>
                                            <ColorPicker
                                                value={theme.tainSendBgColor || '#7AA956'}
                                                onChange={(value) => handleThemeChange('tainSendBgColor', value)}
                                            />
                                        </div>

                                        {/* Send Button Icon Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                ➤ Send Icon Color
                                            </label>
                                            <ColorPicker
                                                value={theme.tainSendIconColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('tainSendIconColor', value)}
                                            />
                                        </div>


                                        {/* Placeholder Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                💬 Placeholder Color
                                            </label>
                                            <ColorPicker
                                                value={theme.tainPlaceholderColor || '#6b7280'}
                                                onChange={(value) => handleThemeChange('tainPlaceholderColor', value)}
                                            />
                                        </div>

                                        {/* Input Container Background */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Container Background
                                            </label>
                                            <ColorPicker
                                                value={theme.tainInputContainerBgColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('tainInputContainerBgColor', value)}
                                            />
                                        </div>

                                        {/* Input Container Border Color */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                🎨 Container Border Color
                                            </label>
                                            <ColorPicker
                                                value={theme.tainInputContainerBorderColor || '#E5E7EB'}
                                                onChange={(value) => handleThemeChange('tainInputContainerBorderColor', value)}
                                            />
                                        </div>

                                        {/* Input Container Padding */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                📏 Container Padding
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="8"
                                                    max="30"
                                                    value={theme.tainInputContainerPadding || 18}
                                                    onChange={(e) => handleThemeChange('tainInputContainerPadding', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.tainInputContainerPadding || 18}px</span>
                                            </div>
                                        </div>

                                        {/* Input Container Gap */}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                ↔️ Container Gap
                                            </label>
                                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                <input
                                                    type="range"
                                                    min="4"
                                                    max="20"
                                                    value={theme.tainInputContainerGap || 8}
                                                    onChange={(e) => handleThemeChange('tainInputContainerGap', e.target.value)}
                                                    style={{flex: 1}}
                                                />
                                                <span style={{minWidth: 35, fontSize: 12, color: '#6b7280'}}>{theme.tainInputContainerGap || 8}px</span>
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>
                        </div>

                        {/* Right: Live Preview */}
                        <div className="widget-preview" style={{background: '#f8fafc', borderLeft: '1px solid #e5e7eb', padding: 20, display: 'flex', flexDirection: 'column', gap: 16}}>
                            <h4 style={{margin: 0, fontSize: 14, fontWeight: 600, color: '#374151'}}>Live Preview</h4>
                            
                            {/* Widget Preview */}
                            <div style={{background: 'white', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                {renderWidgetPreview(widgetKey)}
                            </div>

                            {/* Quick Stats */}
                            <div style={{fontSize: 11, color: '#6b7280', lineHeight: 1.4}}>
                                <div><strong>Background:</strong> {theme[`${widgetKey}BgColor`] || theme.buttonBgColor}</div>
                                <div><strong>Text:</strong> {theme[`${widgetKey}TextColor`] || theme.buttonTextColor}</div>
                                <div><strong>Radius:</strong> {theme[`${widgetKey}BorderRadius`] || 8}px</div>
                                {theme[`${widgetKey}Selector`] && <div><strong>Selector:</strong> {theme[`${widgetKey}Selector`]}</div>}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render widget preview for the right panel
    const renderWidgetPreview = (widgetKey: string) => {
        switch (widgetKey) {
            case 'headerButton':
                return (
                    <button style={{
                        background: theme.headerButtonBgColor || theme.buttonBgColor,
                        color: theme.headerButtonTextColor || theme.buttonTextColor,
                        border: 'none',
                        borderRadius: `${theme.headerButtonBorderRadius || 8}px`,
                        padding: '10px 16px',
                        fontSize: 14,
                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <span style={{fontSize: 16, color: theme.headerButtonIconColor || theme.headerButtonTextColor || theme.buttonTextColor}}>
                            {icons[theme.headerButtonIcon as keyof typeof icons] || icons.robot}
                        </span>
                        {(theme.headerButtonText || 'Chat with AI').replace(/💬|🤖|❓|➤/g, '').trim()}
                    </button>
                );
            
            case 'miniBanner':
                return (
                    <div style={{
                        background: theme.miniBannerBgColor || theme.buttonBgColor,
                        color: theme.miniBannerTextColor || theme.buttonTextColor,
                        borderRadius: `${theme.miniBannerBorderRadius || 12}px`,
                        padding: '8px 16px',
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: theme.miniBannerShadow ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                    }}>
                        <span style={{fontSize: 16}}>
                            {icons[theme.miniBannerIcon as keyof typeof icons] || icons.chat}
                        </span>
                        {(theme.miniBannerText || 'Chat with us').replace(/💬|🤖|❓|➤/g, '').trim()}
                    </div>
                );

            case 'modernBanner':
                return (
                    <div style={{
                        background: theme.modernBannerBgColor || theme.buttonBgColor,
                        color: theme.modernBannerTextColor || theme.buttonTextColor,
                        borderRadius: `${theme.modernBannerBorderRadius || 16}px`,
                        padding: '16px',
                        borderLeft: `${theme.modernBannerLeftBorderWidth || 4}px solid ${theme.modernBannerLeftBorderColor || theme.primaryColor}`,
                        fontSize: 13,
                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        boxShadow: (theme.modernBannerShadowOpacity > 0) 
                            ? `0 4px 12px ${theme.modernBannerShadowColor || 'rgba(0, 0, 0, 0.1)'}`
                            : 'none'
                    }}>
                        <span style={{fontSize: 18, color: theme.modernBannerIconColor || theme.primaryColor}}>
                            {icons[theme.modernBannerIcon as keyof typeof icons] || icons.robot}
                        </span>
                        <div>
                            <div style={{fontWeight: 600, marginBottom: 4}}>
                                {(theme.modernBannerTitle || 'AI Assistant').replace(/💬|🤖|❓|➤/g, '').trim()}
                            </div>
                            <div style={{fontSize: 12, opacity: 0.8}}>
                                {(theme.modernBannerSubtitle || 'Get instant answers').replace(/💬|🤖|❓|➤/g, '').trim()}
                            </div>
                        </div>
                    </div>
                );

            case 'notificationBanner':
                return (
                    <div style={{
                        background: theme.notificationBannerBgColor || theme.buttonBgColor,
                        color: theme.notificationBannerTextColor || theme.buttonTextColor,
                        borderRadius: `${theme.notificationBannerBorderRadius || 12}px`,
                        padding: '12px 16px',
                        fontSize: 13,
                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{color: theme.notificationBannerIconColor || theme.primaryColor, fontSize: 16}}>
                            {icons[theme.notificationBannerIcon as keyof typeof icons] || icons.robot}
                        </div>
                        <div>
                            <div style={{fontWeight: 600, fontSize: 12}}>
                                {(theme.notificationBannerTitle || 'AI Chat Available').replace(/💬|🤖|❓|➤|🔔/g, '').trim()}
                            </div>
                            <div style={{fontSize: 11, opacity: 0.8}}>
                                {(theme.notificationBannerMessage || 'Click to start chatting').replace(/💬|🤖|❓|➤|🔔/g, '').trim()}
                            </div>
                        </div>
                    </div>
                );

            case 'mobileButton':
                return (
                    <button style={{
                        background: theme.mobileButtonBgColor || theme.buttonBgColor,
                        color: theme.mobileButtonIconColor || theme.buttonTextColor,
                        border: 'none',
                        borderRadius: theme.mobileButtonShape === 'circle' ? '50%' : '8px',
                        width: 48,
                        height: 48,
                        fontSize: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {icons[theme.mobileButtonIcon as keyof typeof icons] || icons.chat}
                    </button>
                );

            case 'floatingAvatar':
                const avatarSize = theme.floatingAvatarSize === 'small' ? 40 : 
                                 theme.floatingAvatarSize === 'medium' ? 56 : 
                                 theme.floatingAvatarSize === 'xlarge' ? 80 : 
                                 theme.floatingAvatarSize === 'custom' ? `${theme.floatingAvatarCustomWidth || 10}vw` : 64;
                const borderRadius = theme.floatingAvatarShape === 'circle' ? '50%' : 
                                   theme.floatingAvatarShape === 'square' ? '8px' : 
                                   theme.floatingAvatarShape === 'rounded' ? '16px' : '50%';
                
                return (
                    <div style={{position: 'relative', display: 'inline-block'}}>
                        <button 
                            className={theme.floatingAvatarHoverEffect ? `floating-avatar-${theme.floatingAvatarHoverEffect}` : ''}
                            style={{
                            background: theme.floatingAvatarBgColor || theme.buttonBgColor,
                            color: theme.floatingAvatarIconColor || theme.buttonTextColor,
                            border: `${theme.floatingAvatarBorderWidth || 0}px solid ${theme.floatingAvatarBorderColor || 'transparent'}`,
                            borderRadius: borderRadius,
                            width: avatarSize,
                            height: avatarSize,
                            fontSize: theme.floatingAvatarSize === 'custom' ? `${(parseFloat(theme.floatingAvatarCustomWidth || '10') * 0.4)}vw` : (typeof avatarSize === 'number' ? avatarSize * 0.4 : 24),
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: theme.floatingAvatarShadowIntensity === 'light' ? '0 2px 8px rgba(0,0,0,0.1)' :
                                      theme.floatingAvatarShadowIntensity === 'medium' ? '0 4px 16px rgba(0,0,0,0.15)' :
                                      theme.floatingAvatarShadowIntensity === 'heavy' ? '0 8px 32px rgba(0,0,0,0.2)' : 'none',
                            position: 'relative',
                            animation: theme.floatingAvatarPulseAnimation === 'true' ? 'pulse 2s infinite' : 
                                      theme.floatingAvatarBounceAnimation === 'true' ? 'bounce 2s infinite' : 'none'
                        }}>
                            {theme.floatingAvatarIcon === 'custom' && theme.floatingAvatarCustomImage ? (
                                <img 
                                    src={theme.floatingAvatarCustomImage} 
                                    alt="Avatar" 
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 'inherit'
                                    }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <span style={{fontSize: theme.floatingAvatarSize === 'custom' ? `${(parseFloat(theme.floatingAvatarCustomWidth || '10') * 0.5)}vw` : (typeof avatarSize === 'number' ? avatarSize * 0.5 : 32)}}>
                                    {icons[theme.floatingAvatarIcon as keyof typeof icons] || icons.human}
                                </span>
                            )}
                        </button>
                        {theme.floatingAvatarTooltipText && theme.floatingAvatarTooltipShow !== 'never' && (
                            <>
                                {/* Message Bubble - Beautiful design close to avatar */}
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    ...(theme.floatingAvatarTooltipPosition === 'left' ? {
                                        right: `calc(100% + 12px)`,
                                        left: 'auto'
                                    } : {
                                        left: `calc(100% + 12px)`,
                                        right: 'auto'
                                    }),
                                    background: theme.floatingAvatarTooltipBg || '#ffffff',
                                    color: theme.floatingAvatarTooltipTextColor || '#1f2937',
                                    padding: '16px 20px',
                                    borderRadius: '18px',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    minWidth: '120px',
                                    maxWidth: '280px',
                                    width: 'max-content',
                                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
                                    pointerEvents: 'none',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    opacity: theme.floatingAvatarTooltipShow === 'always' ? 1 : 0.95,
                                    animation: theme.floatingAvatarTooltipAnimation === 'fade' ? 'fadeIn 0.3s ease-in-out' :
                                              theme.floatingAvatarTooltipAnimation === 'slide' ? 'slideIn 0.3s ease-in-out' :
                                              theme.floatingAvatarTooltipAnimation === 'bounce' ? 'bounceIn 0.5s ease-in-out' :
                                              theme.floatingAvatarTooltipAnimation === 'pop' ? 'popIn 0.3s ease-in-out' : 'none',
                                    border: '1px solid rgba(0, 0, 0, 0.05)'
                                }}>
                                    <div style={{
                                        fontWeight: '500',
                                        marginBottom: theme.floatingAvatarTooltipText.includes('\n') ? '8px' : '0'
                                    }}>
                                        {theme.floatingAvatarTooltipText.split('\n')[0]}
                                    </div>
                                    {theme.floatingAvatarTooltipText.includes('\n') && (
                                        <div style={{
                                            fontSize: '13px',
                                            opacity: 0.9
                                        }}>
                                            {theme.floatingAvatarTooltipText.split('\n').slice(1).join('\n')}
                                        </div>
                                    )}
                                    
                                    {/* Speech bubble tail pointing to avatar */}
                                    <div style={{
                                        position: 'absolute',
                                        width: '20px',
                                        height: '20px',
                                        background: theme.floatingAvatarTooltipBg || '#ffffff',
                                        border: '1px solid rgba(0, 0, 0, 0.05)',
                                        borderRight: 'none',
                                        borderTop: 'none',
                                        top: '50%',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        ...(theme.floatingAvatarTooltipPosition === 'left' ? {
                                            right: '-10px',
                                            left: 'auto'
                                        } : {
                                            left: '-10px',
                                            right: 'auto'
                                        })
                                    }} />
                                </div>
                                
                                {/* Display mode indicator for preview */}
                                {theme.floatingAvatarTooltipShow !== 'always' && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: -25,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontSize: 10,
                                        color: '#6b7280',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        ({theme.floatingAvatarTooltipShow === 'hover' ? 'Shows on hover' :
                                          theme.floatingAvatarTooltipShow === 'delay' ? `Shows after ${theme.floatingAvatarTooltipDelay ?? '2'}s` :
                                          theme.floatingAvatarTooltipShow === 'auto_hide' ? `Shows for ${theme.floatingAvatarTooltipDuration ?? '6'}s` :
                                          'Hidden'})
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );

            case 'vancouverBanner':
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: 'clamp(16px, 4vw, 32px)',
                        width: '100%',
                        maxWidth: '100%',
                        background: 'transparent',
                        border: `${theme.interactiveBannerContainerBorderWidth || 0}px solid ${theme.interactiveBannerContainerBorderColor || 'transparent'}`,
                        borderRadius: `${theme.interactiveBannerContainerBorderRadius || 0}px`,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        gap: 'clamp(12px, 3vw, 24px)',
                        boxSizing: 'border-box',
                        boxShadow: (theme.interactiveBannerContainerShadowOpacity > 0) 
                            ? `0 4px 12px ${theme.interactiveBannerContainerShadowColor || 'rgba(0, 0, 0, 0.1)'}`
                            : 'none'
                    }}>
                        {/* Vancouver split background effect (45% magenta, 50% white) */}
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '45%',
                            height: '100%',
                            background: theme.interactiveBannerBgColor || '#f24a91',
                            zIndex: 0
                        }} />
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '50%',
                            height: '100%',
                            background: theme.interactiveBannerSecondaryColor || '#ffffff',
                            zIndex: 0
                        }} />
                        
                        <h1 style={{
                            fontFamily: theme.fontFamily || 'Montserrat, sans-serif',
                            fontWeight: 700,
                            fontSize: 'clamp(24px, 6vw, 42px)',
                            lineHeight: 1.1,
                            margin: '0 0 clamp(8px, 2vw, 16px) 0',
                            color: '#ffffff',
                            textAlign: 'left',
                            cursor: 'pointer',
                            position: 'relative',
                            zIndex: 1,
                            wordBreak: 'break-word'
                        }}>
                            {theme.interactiveBannerTitleFirst || 'Find'}
                            <br />
                            <span style={{color: '#333332'}}>
                                {theme.interactiveBannerTitleSecond || 'yourself'}
                            </span>
                        </h1>
                        
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(6px, 1.5vw, 12px)',
                            background: '#ffffff',
                            border: `1px solid ${theme.interactiveBannerBorderColor || 'rgba(0, 0, 0, 0.1)'}`,
                            borderRadius: 100,
                            padding: 'clamp(10px, 2vw, 14px) clamp(16px, 3vw, 24px)',
                            width: '100%',
                            marginTop: 'clamp(4px, 1vw, 8px)',
                            boxShadow: `0 2px 6px ${theme.interactiveBannerShadowColor || 'rgba(0, 0, 0, 0.05)'}`,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            zIndex: 1,
                            boxSizing: 'border-box'
                        }}>
                            <input
                                type="text"
                                placeholder={theme.interactiveBannerPlaceholder || 'Ask me anything about Vancouver!'}
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    background: 'transparent',
                                    fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                                    fontSize: 'clamp(13px, 2vw, 15px)',
                                    color: '#333332',
                                    outline: 'none',
                                    padding: 0,
                                    margin: 0,
                                    lineHeight: 1.5,
                                    minWidth: 0
                                }}
                                readOnly
                            />
                            <span style={{
                                width: 18,
                                height: 18,
                                color: theme.interactiveBannerButtonColor || '#f24a91',
                                opacity: 0.8,
                                transition: 'opacity 0.2s ease',
                                cursor: 'pointer'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                                </svg>
                            </span>
                        </div>
                        
                        <div style={{
                            fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                            fontSize: 'clamp(10px, 1.5vw, 12px)',
                            fontWeight: 600,
                            color: '#333332',
                            textAlign: 'left',
                            letterSpacing: 1,
                            marginTop: 'clamp(4px, 1vw, 8px)',
                            cursor: 'pointer',
                            position: 'relative',
                            zIndex: 1,
                            wordBreak: 'break-word'
                        }}>
                            {theme.interactiveBannerSubtitle || 'AI-POWERED BRNDSH'}
                        </div>
                    </div>
                );

            case 'tainBanner':
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: 'clamp(12px, 3vw, 20px)',
                        width: '100%',
                        maxWidth: '100%',
                        border: 'none',
                        borderRadius: 'clamp(16px, 4vw, 32px)',
                        cursor: 'pointer',
                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{width: '100%'}}>
                            {/* Input Container - Exact match from TAIN implementation */}
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: `${theme.tainInputContainerGap || 8}px`,
                                backgroundColor: theme.tainInputContainerBgColor || '#ffffff',
                                borderRadius: `${theme[`${widgetKey}BorderRadius`] || 50}px`,
                                padding: `${theme.tainInputContainerPadding || 18}px`,
                                border: `1px solid ${theme.tainInputContainerBorderColor || '#E5E7EB'}`,
                                position: 'relative',
                                boxSizing: 'border-box'
                            }}>
                                {/* Placeholder - only show when input is empty */}
                                {!tainInputValue && (
                                    <div style={{
                                        position: 'absolute',
                                        left: 'clamp(12px, 2vw, 16px)',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: theme.tainPlaceholderColor || '#6b7280',
                                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                                        fontSize: 'clamp(12px, 2vw, 14px)',
                                        pointerEvents: 'none',
                                        lineHeight: 1.5,
                                        textAlign: 'left',
                                        width: 'calc(100% - clamp(60px, 12vw, 80px))',
                                        padding: 'clamp(8px, 2vw, 12px) clamp(12px, 2vw, 16px)',
                                        wordBreak: 'break-word'
                                    }}>
                                        {(theme.tainPlaceholder || 'Hi! I\'m AI Assistant.\nAsk me anything you need help with').split('\n').map((line, i) => (
                                            <div key={i}>{line}</div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* Interactive input field */}
                                <input
                                    type="text"
                                    value={tainInputValue}
                                    onChange={(e) => setTainInputValue(e.target.value)}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        background: 'transparent',
                                        fontFamily: theme.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                                        fontSize: 'clamp(12px, 2vw, 14px)',
                                        padding: 'clamp(8px, 2vw, 12px) clamp(10px, 2vw, 14px)',
                                        fontWeight: 400,
                                        outline: 'none',
                                        color: theme[`${widgetKey}TextColor`] || theme.buttonTextColor || '#393939',
                                        height: 'auto',
                                        minHeight: 'clamp(40px, 8vw, 60px)',
                                        minWidth: 0
                                    }}
                                    placeholder=""
                                />
                                
                                {/* TAIN style send button */}
                                <button style={{
                                    width: 'clamp(36px, 8vw, 54px)',
                                    height: 'clamp(36px, 8vw, 54px)',
                                    padding: 0,
                                    border: 'none',
                                    backgroundColor: theme.tainSendBgColor || '#7AA956',
                                    color: 'white',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                                onClick={() => setTainInputValue('')}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill={theme.tainSendIconColor || '#ffffff'} xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 'voiceWidget':
                // Voice widget preview for e-commerce
                const voiceWidgetTheme = {
                    enabled: true,
                    bannerTrigger: theme.ecommerceBannerTrigger || {
                        enabled: true,
                        bgColor: theme.primaryColor || '#007bff',
                        iconColor: '#ffffff',
                        textColor: '#ffffff',
                        position: 'bottom-right',
                        size: 'medium',
                        shape: 'circle',
                        icon: 'microphone',
                        pulseAnimation: true,
                        tooltipText: 'E-commerce Assistant'
                    },
                    resultsOverlay: theme.ecommerceResultsOverlay || {
                        bgColor: '#ffffff',
                        textColor: '#1f2937',
                        borderColor: '#e5e7eb',
                        borderRadius: '12px',
                        backdropBlur: false,
                        maxWidth: '400px',
                        maxHeight: '600px',
                        animation: 'slide-up'
                    }
                };
                return <VoiceWidgetPreview voiceMode={voiceWidgetTheme} />;

            case 'sidebarWidget':
                return (
                    <SidebarWidgetPreview
                        theme={theme}
                        position={(theme.sidebarPosition === 'left' || theme.sidebarPosition === 'right') ? theme.sidebarPosition : 'right'}
                    />
                );

            default:
                return (
                    <div style={{
                        background: '#f3f4f6',
                        borderRadius: 8,
                        padding: 16,
                        textAlign: 'center',
                        color: '#6b7280',
                        fontSize: 12
                    }}>
                        Widget preview will appear here
                    </div>
                );
        }
    };

    return (
        <div className="theme-editor">
            <style>
                {`
                    @keyframes pulse {
                        0% {
                            box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
                        }
                        70% {
                            box-shadow: 0 0 0 20px rgba(102, 126, 234, 0);
                        }
                        100% {
                            box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
                        }
                    }
                    
                    @keyframes bounce {
                        0%, 20%, 50%, 80%, 100% {
                            transform: translateY(0);
                        }
                        40% {
                            transform: translateY(-10px);
                        }
                        60% {
                            transform: translateY(-5px);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes bounceIn {
                        0% {
                            opacity: 0;
                            transform: scale(0.3);
                        }
                        50% {
                            opacity: 1;
                            transform: scale(1.05);
                        }
                        70% {
                            transform: scale(0.9);
                        }
                        100% {
                            transform: scale(1);
                        }
                    }
                    
                    @keyframes popIn {
                        0% {
                            opacity: 0;
                            transform: scale(0.8);
                        }
                        100% {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    
                    .floating-avatar-scale:hover {
                        transform: scale(1.1);
                        transition: transform 0.3s ease;
                    }
                    
                    .floating-avatar-lift:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 8px 32px rgba(0,0,0,0.2);
                        transition: all 0.3s ease;
                    }
                    
                    .floating-avatar-glow:hover {
                        box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
                        transition: box-shadow 0.3s ease;
                    }
                    
                    .floating-avatar-rotate:hover {
                        transform: rotate(15deg) scale(1.05);
                        transition: transform 0.3s ease;
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            {/* Header */}
            <div className="theme-editor-header" style={{ paddingTop: 8, paddingBottom: 8 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 18, lineHeight: '22px' }}>Widget Theme Studio</h1>
                </div>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    {/* Apply Variants Button - only show if branding is available */}
                    {clientBranding && (
                    <button
                        className="apply-variants-button"
                        onClick={async () => {
                            if (themeVariants.length === 0) {
                                await fetchThemeVariants();
                            }
                            setShowVariantModal(true);
                        }}
                        disabled={isLoadingVariants}
                        style={{
                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: isLoadingVariants ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.25)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            minWidth: 'auto',
                            opacity: isLoadingVariants ? 0.7 : 1
                        }}
                        onMouseOver={(e) => {
                            if (!isLoadingVariants) {
                                (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                                (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.35)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isLoadingVariants) {
                                (e.target as HTMLElement).style.transform = 'translateY(0)';
                                (e.target as HTMLElement).style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.25)';
                            }
                        }}
                        title="Choose from beautiful theme variants generated from your brand colors"
                    >
                        {isLoadingVariants ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="loading-spinner" style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid rgba(255, 255, 255, 0.3)',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite'
                                }} />
                                Loading Variants...
                            </span>
                        ) : (
                            '🎨 Apply Variants'
                        )}
                    </button>
                    )}

                    <button 
                        className="reset-defaults-button"
                        onClick={resetToDefaults}
                        style={{
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontWeight: 600,
                            fontSize: 13,
                            color: '#374151',
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                            transition: 'all 0.2s ease',
                            minWidth: 'auto'
                        }}
                        onMouseOver={(e) => {
                            (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                            (e.target as HTMLElement).style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
                            (e.target as HTMLElement).style.borderColor = '#d1d5db';
                        }}
                        onMouseOut={(e) => {
                            (e.target as HTMLElement).style.transform = 'translateY(0)';
                            (e.target as HTMLElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                            (e.target as HTMLElement).style.borderColor = '#e5e7eb';
                        }}
                    >
                        Reset
                    </button>
                    <button 
                        className="save-button" 
                        onClick={saveTheme}
                        style={{
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)',
                            minWidth: 'auto'
                        }}
                    >
                        💾 Save
                    </button>
                </div>
            </div>


            {/* 1. Brand Basics - Above Tabs */}
            <div className="brand-basics-section" style={{ marginTop: 16, marginBottom: 24 }}>
                <div className="basic-theme-panel" style={{
                    background: 'white',
                    borderRadius: 12,
                    padding: 24,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}>
                    <h3 style={{fontSize: 20, fontWeight: 600, marginBottom: 16, color: '#334155', letterSpacing: '-0.3px'}}>🎨 Brand Basics</h3>
                    <div className="brand-controls" style={{display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap'}}>
                        {(theme.interfaceVariant || 'current') === 'current' && (
                            <>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                            <label style={{fontWeight: 500, color: '#475569', fontSize: 15}}>Primary Colour</label>
                            <ColorPicker
                                value={theme.primaryColor}
                                onChange={(value) => handleThemeChange('primaryColor', value)}
                            />
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                            <label style={{fontWeight: 500, color: '#475569', fontSize: 15}}>Secondary Colour</label>
                            <ColorPicker
                                value={theme.secondaryColor}
                                onChange={(value) => handleThemeChange('secondaryColor', value)}
                            />
                        </div>
                            </>
                        )}
                        <div style={{display: 'flex', flexDirection: 'column', fontWeight: 500, color: '#475569', fontSize: 15, gap: 6}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2}}>
                                <span>🔤 Primary Font</span>
                                <button
                                    onClick={() => setShowFontSelector(!showFontSelector)}
                                    style={{
                                        background: 'none',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 4,
                                        padding: '2px 6px',
                                        fontSize: 11,
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {showFontSelector ? 'Hide All' : 'Show All'}
                                </button>
                            </div>
                            
                            {showFontSelector ? (
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    padding: 16,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    position: 'relative',
                                    zIndex: 10
                                }}>
                                    {/* Preview Text Input */}
                                    <input
                                        type="text"
                                        value={previewText}
                                        onChange={(e) => setPreviewText(e.target.value)}
                                        placeholder="Type to preview fonts..."
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14,
                                            marginBottom: 12
                                        }}
                                    />
                                    
                                    {/* Font Categories */}
                                    <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap'}}>
                                        {Object.entries(FONT_CATEGORIES).map(([key, label]) => (
                                            <button
                                                key={key}
                                                onClick={() => setSelectedFontCategory(key)}
                                                style={{
                                                    padding: '4px 12px',
                                                    fontSize: 12,
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 16,
                                                    background: selectedFontCategory === key ? '#667eea' : 'white',
                                                    color: selectedFontCategory === key ? 'white' : '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => setSelectedFontCategory('popular')}
                                            style={{
                                                padding: '4px 12px',
                                                fontSize: 12,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 16,
                                                background: selectedFontCategory === 'popular' ? '#667eea' : 'white',
                                                color: selectedFontCategory === 'popular' ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            ⭐ Popular
                                        </button>
                                    </div>
                                    
                                    {/* Font Grid */}
                                    <div style={{display: 'grid', gap: 8, maxHeight: 240, overflow: 'auto'}}>
                                        {(selectedFontCategory === 'popular' ? POPULAR_FONTS : getFontsByCategory(selectedFontCategory)).map(font => (
                                            <div
                                                key={font.name}
                                                onClick={() => {
                                                    handleThemeChange('fontFamily', `'${font.name}', ${font.category === 'serif' ? 'serif' : 'sans-serif'}`);
                                                    setShowFontSelector(false);
                                                }}
                                                style={{
                                                    padding: '12px',
                                                    border: theme.fontFamily?.includes(font.name) ? '2px solid #667eea' : '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    cursor: 'pointer',
                                                    background: theme.fontFamily?.includes(font.name) ? '#f0f4ff' : 'white',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!theme.fontFamily?.includes(font.name)) {
                                                        (e.target as HTMLElement).style.borderColor = '#cbd5e1';
                                                        (e.target as HTMLElement).style.background = '#f8fafc';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!theme.fontFamily?.includes(font.name)) {
                                                        (e.target as HTMLElement).style.borderColor = '#e5e7eb';
                                                        (e.target as HTMLElement).style.background = 'white';
                                                    }
                                                }}
                                            >
                                                <div style={{
                                                    fontFamily: `'${font.name}', ${font.category === 'serif' ? 'serif' : 'sans-serif'}`,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                    color: '#1f2937'
                                                }}>
                                                    {font.name}
                                                </div>
                                                <div style={{
                                                    fontFamily: `'${font.name}', ${font.category === 'serif' ? 'serif' : 'sans-serif'}`,
                                                    fontSize: 13,
                                                    color: '#6b7280',
                                                    marginBottom: 4
                                                }}>
                                                    {previewText}
                                                </div>
                                                <div style={{
                                                    fontSize: 11,
                                                    color: '#9ca3af',
                                                    fontStyle: 'italic'
                                                }}>
                                                    {font.usage}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    padding: '8px 12px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    background: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer'
                                }} onClick={() => setShowFontSelector(true)}>
                                    <span style={{
                                        fontFamily: theme.fontFamily || 'Inter, sans-serif',
                                        fontSize: 14
                                    }}>
                                        {theme.fontFamily?.split(',')[0].replace(/['"]/g, '').trim() || 'Inter'}
                                    </span>
                                    <span style={{fontSize: 12, color: '#9ca3af'}}>Click to change</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Tabs */}
            <div className="tabs" style={{marginBottom: 32}}>
                <button
                    className={`tab ${activeTab === 'widgetTrigger' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('widgetTrigger');
                        localStorage.setItem('themeEditorActiveTab', 'widgetTrigger');
                    }}
                >
                    📱 Widget Trigger
                </button>
                <button
                    className={`tab ${activeTab === 'chatInterface' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('chatInterface');
                        localStorage.setItem('themeEditorActiveTab', 'chatInterface');
                    }}
                >
                    💬 Chat Interface
                </button>
            </div>

            {/* 3. Widget Types – Only show when Widget Trigger tab is active */}
            {activeTab === 'widgetTrigger' && (
                <div className="widget-types-section" style={{background: 'none', boxShadow: 'none', marginBottom: 32}}>
                    <h3 style={{fontSize: 20, fontWeight: 600, marginBottom: 20, color: '#334155', letterSpacing: '-0.5px'}}>📱 Widget Types</h3>
                    <div className="widget-type-blocks" style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                        {availableWidgets
                            .filter(widget => {
                                const variant = theme.interfaceVariant || 'current';
                                if (variant === 'standard') return widget.key === 'floatingAvatar';
                                if (variant === 'ecommerce') return widget.key === 'searchBarTrigger';
                                return true; // Show all for 'current' variant
                            })
                            .map(widget => (
                            <div
                                key={widget.key}
                                className={`widget-type-block ${selectedTriggers.includes(widget.key) ? 'selected' : ''}`}
                                onClick={() => toggleWidget(widget.key)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '12px 18px',
                                    borderRadius: 10,
                                    border: selectedTriggers.includes(widget.key) ? '2px solid #6366f1' : '1px solid #e5e7eb',
                                    background: selectedTriggers.includes(widget.key) ? 'rgba(99, 102, 241, 0.05)' : 'white',
                                    fontWeight: 500,
                                    fontSize: 15,
                                    color: selectedTriggers.includes(widget.key) ? '#6366f1' : '#334155',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    minWidth: 0,
                                    minHeight: 0,
                                    boxShadow: selectedTriggers.includes(widget.key) ? '0 4px 12px rgba(99, 102, 241, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.05)',
                                    marginBottom: 0
                                }}
                            >
                                <span className="widget-icon" style={{fontSize: 18}}>{widget.icon}</span>
                                <span className="widget-name" style={{fontSize: 15}}>{widget.name}</span>
                                {selectedTriggers.includes(widget.key) && (
                                    <span className="selected-indicator" style={{marginLeft: 2, fontSize: 15}}>✓</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Content based on active tab */}
            <div className="editor-container" style={{padding: 0}}>
                {activeTab === 'widgetTrigger' && (
                    <>
                        {/* 5. Selected Widgets - Individual Blocks with Cross Icons */}
                        {selectedTriggers.length > 0 && (
                            <div className="selected-widgets-section">
                                <h3>⚙️ Selected Widgets</h3>
                                <div className="selected-widgets-list">
                                    {selectedTriggers.map(trigger => renderSelectedWidget(trigger))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'chatInterface' && (
                    <div className="chat-interface-section">
                        <h3>💬 Chat Interface Settings</h3>
                        {/* Variant Selector */}
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#374151'}}>
                                🧩 Interface Variant
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 12}}>
                                Choose between modal popup, full-height sidebar, or e-commerce assistant.
                            </p>
                            <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                    <input
                                        type="radio"
                                        name="interfaceVariant"
                                        value="current"
                                        checked={(theme.interfaceVariant || 'current') === 'current'}
                                        onChange={(e) => handleThemeChange('interfaceVariant', e.target.value)}
                                    />
                                    <span style={{fontSize: 14, color: '#374151'}}>Current (modal)</span>
                                </label>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: theme.interfaceVariant === 'standard' ? '#EFF6FF' : 'transparent', padding: '8px 12px', borderRadius: '8px', border: theme.interfaceVariant === 'standard' ? '2px solid #3B82F6' : '2px solid transparent'}}>
                                    <input
                                        type="radio"
                                        name="interfaceVariant"
                                        value="standard"
                                        checked={theme.interfaceVariant === 'standard'}
                                        onChange={(e) => { 
                                            handleThemeChange('interfaceVariant', e.target.value); 
                                            if (e.target.value === 'standard') {
                                                handleThemeChange('standardPanelBg', '#FFFFFF');
                                                handleThemeChange('standardHeaderBg', '#FFFFFF');
                                            }
                                            setSelectedTriggers(['floatingAvatar']); 
                                        }}
                                    />
                                    <span style={{fontSize: 14, color: '#374151', fontWeight: theme.interfaceVariant === 'standard' ? 600 : 400}}>⭐ Standard (full-height sidebar)</span>
                                </label>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: theme.interfaceVariant === 'ecommerce' ? '#EFF6FF' : 'transparent', padding: '8px 12px', borderRadius: '8px', border: theme.interfaceVariant === 'ecommerce' ? '2px solid #3B82F6' : '2px solid transparent'}}>
                                    <input
                                        type="radio"
                                        name="interfaceVariant"
                                        value="ecommerce"
                                        checked={theme.interfaceVariant === 'ecommerce'}
                                        onChange={(e) => { handleThemeChange('interfaceVariant', e.target.value); setSelectedTriggers(['searchBarTrigger']); }}
                                    />
                                    <span style={{fontSize: 14, color: '#374151', fontWeight: theme.interfaceVariant === 'ecommerce' ? 600 : 400}}>🛒 E-commerce Assistant</span>
                                </label>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: theme.interfaceVariant === 'proactive' ? '#EFF6FF' : 'transparent', padding: '8px 12px', borderRadius: '8px', border: theme.interfaceVariant === 'proactive' ? '2px solid #3B82F6' : '2px solid transparent'}}>
                                    <input
                                        type="radio"
                                        name="interfaceVariant"
                                        value="proactive"
                                        checked={theme.interfaceVariant === 'proactive'}
                                        onChange={(e) => { handleThemeChange('interfaceVariant', e.target.value); }}
                                    />
                                    <span style={{fontSize: 14, color: '#374151', fontWeight: theme.interfaceVariant === 'proactive' ? 600 : 400}}>🚀 Proactive (auto-open with chips)</span>
                                </label>
                            </div>

                            {/* Proactive variant settings: agent name, welcome message, chips */}
                            {theme.interfaceVariant === 'proactive' && (
                                <div style={{marginTop: 16, padding: 16, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB'}}>
                                    <h5 style={{fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#374151'}}>
                                        Welcome panel content
                                    </h5>
                                    <p style={{fontSize: 12, color: '#6b7280', marginBottom: 12}}>
                                        Shown when the chat auto-opens, before the visitor sends a message.
                                    </p>

                                    <label style={{display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4}}>Agent name (header)</label>
                                    <input
                                        type="text"
                                        value={theme.botAgentName || ''}
                                        onChange={(e) => handleThemeChange('botAgentName', e.target.value)}
                                        placeholder="e.g. Jessica"
                                        style={{width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, marginBottom: 12, boxSizing: 'border-box'}}
                                    />

                                    <label style={{display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4}}>Welcome message</label>
                                    <textarea
                                        value={theme.welcomeMessage || ''}
                                        onChange={(e) => handleThemeChange('welcomeMessage', e.target.value)}
                                        placeholder="👋 Hi there! How can I help you today?"
                                        rows={2}
                                        style={{width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical'}}
                                    />

                                    <label style={{display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4}}>Quick-reply chips (up to 4)</label>
                                    {[0, 1, 2, 3].map((idx) => (
                                        <input
                                            key={idx}
                                            type="text"
                                            value={(theme.welcomeChips && theme.welcomeChips[idx]) || ''}
                                            onChange={(e) => {
                                                const next = Array.isArray(theme.welcomeChips) ? [...theme.welcomeChips] : ['', '', '', ''];
                                                while (next.length < 4) next.push('');
                                                next[idx] = e.target.value;
                                                // Trim trailing empties from the saved value so the widget renders only filled chips
                                                const trimmed = [...next];
                                                while (trimmed.length && !trimmed[trimmed.length - 1]) trimmed.pop();
                                                handleThemeChange('welcomeChips', trimmed);
                                            }}
                                            placeholder={['I have a question', 'Talk to sales', 'I need support', 'Pricing info'][idx]}
                                            style={{width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, marginBottom: 6, boxSizing: 'border-box'}}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Position Selector - shown for Standard (full-height sidebar)
                            and Proactive (auto-open floating panel). Both variants
                            already read theme.interfacePosition at runtime. */}
                        {/* Agent name (header) for the Standard (sidebar) variant.
                            Proactive has its own agent-name field in the welcome-panel
                            section above, so this is shown for standard only. Empty =
                            fall back to the client's display name (no behavior change). */}
                        {theme.interfaceVariant === 'standard' && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>🏷️ Bot Name</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 12}}>
                                    Name shown in the sidebar header. Leave blank to use your default display name.
                                </p>
                                <input
                                    type="text"
                                    value={theme.botAgentName || ''}
                                    onChange={(e) => handleThemeChange('botAgentName', e.target.value)}
                                    placeholder="e.g. Secure Set Helper"
                                    style={{width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 6, boxSizing: 'border-box'}}
                                />
                            </div>
                        )}

                        {(theme.interfaceVariant === 'standard' || theme.interfaceVariant === 'proactive') && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#374151'}}>📍 {theme.interfaceVariant === 'proactive' ? 'Panel Position' : 'Sidebar Position'}</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 12}}>
                                    {theme.interfaceVariant === 'proactive'
                                        ? 'Choose which side of the screen the welcome panel and chat appear on'
                                        : 'Choose which side of the screen the sidebar appears on'}
                                </p>
                                <div style={{display: 'flex', gap: 12}}>
                                    <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', background: (theme.interfacePosition || 'right') === 'right' ? '#EFF6FF' : 'transparent', borderRadius: 8, border: (theme.interfacePosition || 'right') === 'right' ? '2px solid #3B82F6' : '2px solid #e5e7eb'}}>
                                        <input
                                            type="radio"
                                            name="interfacePosition"
                                            value="right"
                                            checked={(theme.interfacePosition || 'right') === 'right'}
                                            onChange={(e) => handleThemeChange('interfacePosition', e.target.value)}
                                        />
                                        <span style={{fontSize: 14, color: '#374151', fontWeight: (theme.interfacePosition || 'right') === 'right' ? 600 : 400}}>→ Right Side</span>
                                    </label>
                                    <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 16px', background: theme.interfacePosition === 'left' ? '#EFF6FF' : 'transparent', borderRadius: 8, border: theme.interfacePosition === 'left' ? '2px solid #3B82F6' : '2px solid #e5e7eb'}}>
                                        <input
                                            type="radio"
                                            name="interfacePosition"
                                            value="left"
                                            checked={theme.interfacePosition === 'left'}
                                            onChange={(e) => handleThemeChange('interfacePosition', e.target.value)}
                                        />
                                        <span style={{fontSize: 14, color: '#374151', fontWeight: theme.interfacePosition === 'left' ? 600 : 400}}>← Left Side</span>
                                    </label>
                                </div>
                            </div>
                        )}
                        
                        {/* Container Background (Current variant only) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                🎨 Container Background
                            </h4>
                            
                            {/* Toggle for gradient vs solid */}
                            <div style={{marginBottom: 16}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                    <input
                                        type="checkbox"
                                        checked={theme.chatContainerUseGradient === 'true'}
                                        onChange={(e) => handleThemeChange('chatContainerUseGradient', String(e.target.checked))}
                                        style={{width: 16, height: 16}}
                                    />
                                    <span style={{fontSize: 14, color: '#374151'}}>Use gradient background</span>
                                </label>
                            </div>
                            
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                {theme.chatContainerUseGradient !== 'true' ? (
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            Background Color
                                        </label>
                                        <ColorPicker
                                            value={theme.chatContainerBg || '#374151'}
                                            onChange={(value) => handleThemeChange('chatContainerBg', value)}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Gradient Start Color
                                            </label>
                                            <ColorPicker
                                                value={theme.chatContainerGradientStart || '#1e293b'}
                                                onChange={(value) => handleThemeChange('chatContainerGradientStart', value)}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Gradient End Color
                                            </label>
                                            <ColorPicker
                                                value={theme.chatContainerGradientEnd || '#334155'}
                                                onChange={(value) => handleThemeChange('chatContainerGradientEnd', value)}
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {/* Opacity Slider */}
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Background Opacity
                                    </label>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={theme.chatContainerOpacity || 100}
                                            onChange={(e) => handleThemeChange('chatContainerOpacity', e.target.value)}
                                            style={{
                                                flex: 1,
                                                height: 6,
                                                borderRadius: 3,
                                                background: '#e5e7eb',
                                                outline: 'none',
                                                WebkitAppearance: 'none',
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <span style={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#6b7280',
                                            minWidth: 45,
                                            textAlign: 'right'
                                        }}>
                                            {theme.chatContainerOpacity || 100}%
                                        </span>
                                    </div>
                                    <div style={{
                                        fontSize: 11,
                                        color: '#9ca3af',
                                        marginTop: 4
                                    }}>
                                        Lower values make background more transparent
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Text and UI Colors (applies to both variants) */}
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                ✏️ Text and UI Colors
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Chat Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatTextColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatTextColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Send Icon Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSendIconColor || '#667eea'}
                                        onChange={(value) => handleThemeChange('chatSendIconColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Send Button Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSendButtonBg || '#667eea'}
                                        onChange={(value) => handleThemeChange('chatSendButtonBg', value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Inline Sources customization is not needed for Standard – hide it */}

                        {/* Standard Panel, Bubbles & Header */}
                        {(theme.interfaceVariant || 'current') === 'standard' && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>🎨 Colors & Styling</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>Essential color customizations for your Standard sidebar</p>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Panel Background</label>
                                        <ColorPicker
                                            value={theme.standardPanelBg || '#EEF2F7'}
                                            onChange={(value) => handleThemeChange('standardPanelBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Header Background</label>
                                        <ColorPicker
                                            value={theme.standardHeaderBg || '#FFFFFF'}
                                            onChange={(value) => handleThemeChange('standardHeaderBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Header Text</label>
                                        <ColorPicker
                                            value={theme.standardHeaderText || '#111827'}
                                            onChange={(value) => handleThemeChange('standardHeaderText', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Primary Brand Color</label>
                                        <ColorPicker
                                            value={theme.primaryColor || '#667eea'}
                                            onChange={(value) => handleThemeChange('primaryColor', value)}
                                        />
                                    </div>
                                </div>

                                {/* Message Bubble Colors */}
                                <h5 style={{fontSize: 14, fontWeight: 600, marginTop: 20, marginBottom: 12, color: '#374151'}}>💬 Message Bubbles</h5>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>User Message Background</label>
                                        <ColorPicker
                                            value={theme.standardUserBubbleBg || '#2563EB'}
                                            onChange={(value) => handleThemeChange('standardUserBubbleBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>User Message Text</label>
                                        <ColorPicker
                                            value={theme.standardUserBubbleText || '#FFFFFF'}
                                            onChange={(value) => handleThemeChange('standardUserBubbleText', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Agent Reply Background</label>
                                        <ColorPicker
                                            value={theme.standardBotBubbleBg || '#FFFFFF'}
                                            onChange={(value) => handleThemeChange('standardBotBubbleBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Agent Reply Text</label>
                                        <ColorPicker
                                            value={theme.standardBotBubbleText || '#111827'}
                                            onChange={(value) => handleThemeChange('standardBotBubbleText', value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bot Icon Selector (only for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'standard' && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>🤖 Bot Icon</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>Choose a modern bot icon for your assistant</p>

                                {/* Icon Type Selector */}
                                <div style={{marginBottom: 16}}>
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8}}>Icon Style</label>
                                    <select
                                        value={theme.standardBotIcon || 'ai-chip'}
                                        onChange={(e) => handleThemeChange('standardBotIcon', e.target.value)}
                                        style={{width:'100%', padding:'10px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14, background:'white'}}
                                    >
                                        <option value="ai-chip">🔷 AI Chip</option>
                                        <option value="ai-assistant">😊 AI Assistant</option>
                                        <option value="chat-bot">💬 Chat Bot</option>
                                        <option value="sparkle-ai">✨ Sparkle AI</option>
                                        <option value="neural-net">🧠 Neural Network</option>
                                        <option value="rocket">🚀 Rocket</option>
                                        <option value="custom">🖼️ Custom Image</option>
                                    </select>
                                </div>

                                {/* Icon Preview */}
                                <div style={{marginBottom: 16, padding: 16, background: '#f9fafb', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12}}>
                                    <span style={{fontSize: 12, fontWeight: 500, color: '#6b7280'}}>Preview:</span>
                                    <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                        {/* Preview bot icon at different sizes */}
                                        <div style={{padding: 8, background: 'white', borderRadius: 6, border: '1px solid #e5e7eb'}}>
                                            <div dangerouslySetInnerHTML={{
                                                __html: `
                                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                                        ${theme.standardBotIcon === 'ai-chip' || !theme.standardBotIcon ? `
                                                            <rect x="8" y="8" width="16" height="16" rx="2" fill="url(#grad1)" stroke="#5a67d8" stroke-width="1.5"/>
                                                            <path d="M12 12h8M12 16h8M12 20h8" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                                                            <circle cx="16" cy="16" r="2" fill="#fbbf24"/>
                                                            <defs><linearGradient id="grad1" x1="8" y1="8" x2="24" y2="24"><stop offset="0%" stop-color="#667eea"/><stop offset="100%" stop-color="#764ba2"/></linearGradient></defs>
                                                        ` : theme.standardBotIcon === 'ai-assistant' ? `
                                                            <circle cx="16" cy="16" r="12" fill="url(#grad2)" stroke="#5a67d8" stroke-width="1.5"/>
                                                            <circle cx="12" cy="14" r="2" fill="white"/>
                                                            <circle cx="20" cy="14" r="2" fill="white"/>
                                                            <path d="M12 20 Q16 23 20 20" stroke="white" stroke-width="2" stroke-linecap="round"/>
                                                            <defs><linearGradient id="grad2" x1="4" y1="4" x2="28" y2="28"><stop offset="0%" stop-color="#8b5cf6"/><stop offset="100%" stop-color="#6366f1"/></linearGradient></defs>
                                                        ` : theme.standardBotIcon === 'chat-bot' ? `
                                                            <rect x="4" y="6" width="24" height="18" rx="4" fill="url(#grad3)" stroke="#2563eb" stroke-width="1.5"/>
                                                            <circle cx="11" cy="15" r="2" fill="white"/>
                                                            <circle cx="16" cy="15" r="2" fill="white"/>
                                                            <circle cx="21" cy="15" r="2" fill="white"/>
                                                            <defs><linearGradient id="grad3" x1="4" y1="6" x2="28" y2="24"><stop offset="0%" stop-color="#3b82f6"/><stop offset="100%" stop-color="#1d4ed8"/></linearGradient></defs>
                                                        ` : theme.standardBotIcon === 'sparkle-ai' ? `
                                                            <path d="M16 4L17.5 11L24 12.5L17.5 14L16 21L14.5 14L8 12.5L14.5 11L16 4Z" fill="url(#grad4)" stroke="#d97706" stroke-width="1.5" stroke-linejoin="round"/>
                                                            <path d="M24 6L25 9L28 10L25 11L24 14L23 11L20 10L23 9L24 6Z" fill="#fbbf24"/>
                                                            <defs><linearGradient id="grad4" x1="8" y1="4" x2="24" y2="21"><stop offset="0%" stop-color="#f59e0b"/><stop offset="100%" stop-color="#d97706"/></linearGradient></defs>
                                                        ` : theme.standardBotIcon === 'neural-net' ? `
                                                            <circle cx="8" cy="8" r="3" fill="#10b981" stroke="#059669" stroke-width="1.5"/>
                                                            <circle cx="24" cy="8" r="3" fill="#10b981" stroke="#059669" stroke-width="1.5"/>
                                                            <circle cx="16" cy="16" r="4" fill="url(#grad5)" stroke="#047857" stroke-width="1.5"/>
                                                            <path d="M11 10 L14 14 M21 10 L18 14" stroke="#34d399" stroke-width="1.5" stroke-linecap="round"/>
                                                            <defs><linearGradient id="grad5" x1="12" y1="12" x2="20" y2="20"><stop offset="0%" stop-color="#34d399"/><stop offset="100%" stop-color="#10b981"/></linearGradient></defs>
                                                        ` : theme.standardBotIcon === 'rocket' ? `
                                                            <path d="M16 4 L20 12 L16 20 L12 12 Z" fill="url(#grad6)" stroke="#dc2626" stroke-width="1.5"/>
                                                            <ellipse cx="16" cy="12" rx="3" ry="4" fill="#fca5a5"/>
                                                            <circle cx="16" cy="11" r="2" fill="white"/>
                                                            <defs><linearGradient id="grad6" x1="16" y1="4" x2="16" y2="20"><stop offset="0%" stop-color="#ef4444"/><stop offset="100%" stop-color="#dc2626"/></linearGradient></defs>
                                                        ` : ''}
                                                    </svg>
                                                `
                                            }} />
                                        </div>
                                        <span style={{fontSize: 11, color: '#9ca3af'}}>32px</span>
                                    </div>
                                </div>

                                {/* Custom Image Upload (only if custom is selected) */}
                                {theme.standardBotIcon === 'custom' && (
                                    <div style={{marginTop: 16, padding: 16, background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe'}}>
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8}}>
                                            🖼️ Custom Bot Icon Image
                                        </label>
                                        <p style={{fontSize: 11, color: '#6b7280', marginBottom: 12}}>
                                            Upload your custom bot icon (recommended: 64x64px, SVG/PNG with transparent background)
                                        </p>

                                        {/* Image URL Input */}
                                        <input
                                            type="text"
                                            placeholder="Enter image URL or upload below"
                                            value={theme.standardBotCustomImage || ''}
                                            onChange={(e) => handleThemeChange('standardBotCustomImage', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 6,
                                                fontSize: 13,
                                                marginBottom: 12
                                            }}
                                        />

                                        {/* File Upload */}
                                        <input
                                            type="file"
                                            accept="image/svg+xml,image/png,image/jpg,image/jpeg,image/gif"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        const dataUrl = reader.result as string;
                                                        handleThemeChange('standardBotCustomImage', dataUrl);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                            style={{fontSize: 12}}
                                        />

                                        {/* Preview */}
                                        {theme.standardBotCustomImage && (
                                            <div style={{marginTop: 12, padding: 8, background: 'white', borderRadius: 6, display: 'inline-block'}}>
                                                <img src={theme.standardBotCustomImage} alt="Custom bot icon" style={{width: 32, height: 32, objectFit: 'contain'}} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Branding Toggle (available in ALL variants and ALL plans) */}
                        {(() => {
                            // Branding hide is available on every plan and every interface variant.
                            // The widget footer (Widget.tsx) honors theme.standardHideBranding /
                            // theme.hideBranding regardless of variant or plan, and the backend
                            // persists the flag without any plan gating, so enabling the checkbox
                            // here makes it work end-to-end (including the Free tier).
                            const canHideBranding = true;
                            const planDisplayName = clientPlan?.display_name || (clientPlanTier.charAt(0).toUpperCase() + clientPlanTier.slice(1));

                            return (
                                <div style={{
                                    background: 'white',
                                    borderRadius: 12,
                                    padding: 24,
                                    marginBottom: 24,
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                }}>
                                    <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>🏷️ Branding</h4>
                                    <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                        Control the "Powered by brndsh" footer visibility
                                    </p>

                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        cursor: canHideBranding ? 'pointer' : 'not-allowed',
                                        padding: 12,
                                        background: canHideBranding ? '#f9fafb' : '#fef2f2',
                                        borderRadius: 8,
                                        border: `1px solid ${canHideBranding ? '#e5e7eb' : '#fecaca'}`,
                                        opacity: canHideBranding ? 1 : 0.7
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={(theme.standardHideBranding as any) === 'true' || (theme.standardHideBranding as any) === true}
                                            onChange={(e) => {
                                                if (canHideBranding) {
                                                    handleThemeChange('standardHideBranding', String(e.target.checked));
                                                }
                                            }}
                                            disabled={!canHideBranding}
                                            style={{width: 18, height: 18, cursor: canHideBranding ? 'pointer' : 'not-allowed'}}
                                        />
                                        <div style={{flex: 1}}>
                                            <div style={{fontSize: 14, fontWeight: 500, color: canHideBranding ? '#374151' : '#991b1b'}}>
                                                Hide "Powered by brndsh" footer
                                            </div>
                                            <div style={{fontSize: 11, color: canHideBranding ? '#6b7280' : '#dc2626', marginTop: 2}}>
                                                {canHideBranding ? (
                                                    ((theme.standardHideBranding as any) === 'true' || (theme.standardHideBranding as any) === true) ? (
                                                        <>✅ Branding hidden - White label active on your {planDisplayName} plan</>
                                                    ) : (
                                                        <>⚪ Branding visible - You can hide it with White Label on your {planDisplayName} plan</>
                                                    )
                                                ) : (
                                                    <>🔒 White Label not available on {planDisplayName} plan - "Powered by brndsh" will be shown</>
                                                )}
                                            </div>
                                        </div>
                                        {!canHideBranding && (
                                            <a
                                                href="/client/billing?tab=plans"
                                                style={{
                                                    fontSize: 11,
                                                    color: '#3b82f6',
                                                    textDecoration: 'none',
                                                    fontWeight: 600,
                                                    padding: '4px 8px',
                                                    background: '#eff6ff',
                                                    borderRadius: 4,
                                                    border: '1px solid #bfdbfe'
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Upgrade
                                            </a>
                                        )}
                                    </label>
                                </div>
                            );
                        })()}

                        {/* Citation Links (only for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'standard' && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>🔗 Citation Links</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>Customize inline citation links (Grok-style). Citations appear as colored pills embedded in the response text.</p>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Citation Text Color</label>
                                        <ColorPicker
                                            value={theme.standardCitationLinkColor || '#2563EB'}
                                            onChange={(value) => handleThemeChange('standardCitationLinkColor', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Citation Background</label>
                                        <ColorPicker
                                            value={theme.standardCitationLinkBg || 'rgba(37, 99, 235, 0.08)'}
                                            onChange={(value) => handleThemeChange('standardCitationLinkBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Citation Border</label>
                                        <ColorPicker
                                            value={theme.standardCitationLinkBorder || 'rgba(37, 99, 235, 0.2)'}
                                            onChange={(value) => handleThemeChange('standardCitationLinkBorder', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Citation Hover Background</label>
                                        <ColorPicker
                                            value={theme.standardCitationLinkHoverBg || 'rgba(37, 99, 235, 0.15)'}
                                            onChange={(value) => handleThemeChange('standardCitationLinkHoverBg', value)}
                                        />
                                    </div>
                                </div>

                                {/* Preview */}
                                <div style={{marginTop: 16, padding: 16, background: '#f9fafb', borderRadius: 8}}>
                                    <p style={{fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8}}>Preview:</p>
                                    <div style={{fontSize: 14, color: '#111827', lineHeight: 1.6}}>
                                        Based on recent studies
                                        <a
                                            href="#"
                                            onClick={(e) => e.preventDefault()}
                                            style={{
                                                color: theme.standardCitationLinkColor || '#2563EB',
                                                background: theme.standardCitationLinkBg || 'rgba(37, 99, 235, 0.08)',
                                                border: `1px solid ${theme.standardCitationLinkBorder || 'rgba(37, 99, 235, 0.2)'}`,
                                                padding: '2px 8px 2px 6px',
                                                borderRadius: '6px',
                                                textDecoration: 'none',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                margin: '0 2px',
                                                whiteSpace: 'nowrap',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{flexShrink: 0}}>
                                                <path d="M10 14L21 3M21 3h-7m7 0v7" stroke={theme.standardCitationLinkColor || '#2563EB'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            Research Paper
                                        </a>
                                        , the findings show positive results.
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Loading State Customization (only for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'standard' && (
                            <div style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 24,
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                            }}>
                                <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>⏳ Loading State</h4>
                                <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>Customize the loading animation and text shown while generating responses</p>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Loading Icon</label>
                                        <select
                                            value={theme.standardLoadingIcon || 'spinner'}
                                            onChange={(e) => handleThemeChange('standardLoadingIcon', e.target.value)}
                                            style={{width:'100%', padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:14, background:'white'}}
                                        >
                                            <option value="spinner">🔄 Spinner</option>
                                            <option value="dots">••• Dots</option>
                                        </select>
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Loading Text</label>
                                        <input
                                            type="text"
                                            value={theme.standardLoadingText || 'Generating response...'}
                                            onChange={(e) => handleThemeChange('standardLoadingText', e.target.value)}
                                            placeholder="Generating response..."
                                            style={{width:'100%', padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:6, fontSize:14}}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* UI Elements Styling (Current variant only) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                🎯 UI Elements
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Close Icon Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatCloseIconColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatCloseIconColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Close Icon Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatCloseIconBg || '#374151'}
                                        onChange={(value) => handleThemeChange('chatCloseIconBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Message Response Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatMessageResponseBg || '#1e293b'}
                                        onChange={(value) => handleThemeChange('chatMessageResponseBg', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Feedback Icons (Standard + Current + Proactive) */}
                        {(
                            (theme.interfaceVariant || 'current') === 'current' ||
                            (theme.interfaceVariant || 'current') === 'standard' ||
                            (theme.interfaceVariant || 'current') === 'proactive'
                        ) && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                👍 Feedback Icons
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Icon Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackIconColor || '#9ca3af'}
                                        onChange={(value) => handleThemeChange('chatFeedbackIconColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Hover Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackIconHoverColor || '#6b7280'}
                                        onChange={(value) => handleThemeChange('chatFeedbackIconHoverColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Active Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackActiveColor || '#3b82f6'}
                                        onChange={(value) => handleThemeChange('chatFeedbackActiveColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackBg || 'transparent'}
                                        onChange={(value) => handleThemeChange('chatFeedbackBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Hover Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackHoverBg || 'rgba(107, 114, 128, 0.1)'}
                                        onChange={(value) => handleThemeChange('chatFeedbackHoverBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Active Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackActiveBg || 'rgba(59, 130, 246, 0.1)'}
                                        onChange={(value) => handleThemeChange('chatFeedbackActiveBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Border Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackBorderColor || 'transparent'}
                                        onChange={(value) => handleThemeChange('chatFeedbackBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Hover Border
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackHoverBorderColor || '#e5e7eb'}
                                        onChange={(value) => handleThemeChange('chatFeedbackHoverBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Active Border
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackActiveBorderColor || '#3b82f6'}
                                        onChange={(value) => handleThemeChange('chatFeedbackActiveBorderColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Feedback Tags (What went wrong bubbles) */}
                        {(
                            (theme.interfaceVariant || 'current') === 'current' ||
                            (theme.interfaceVariant || 'current') === 'standard'
                        ) && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                🏷️ Feedback Tags
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                Customize the "What went wrong?" tag bubbles that appear after thumbs down feedback.
                            </p>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tag Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagBg || '#364356'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tag Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tag Border Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagBorderColor || '#475569'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Hover Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagHoverBg || '#4a5568'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagHoverBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Hover Border Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagHoverBorderColor || '#718096'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagHoverBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Active Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagActiveBg || '#2d3748'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagActiveBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Label Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackTagLabelColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatFeedbackTagLabelColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Thank You Message Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatFeedbackThankYouColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatFeedbackThankYouColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Sources in Response (hide for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                💬 Sources in Response
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                Customize how sources appear within chat responses
                            </p>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Source Item Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesResponseBg || '#f9fafb'}
                                        onChange={(value) => handleThemeChange('chatSourcesResponseBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Widget Container Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesScrollBg || '#f3f4f6'}
                                        onChange={(value) => handleThemeChange('chatSourcesScrollBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Widget Container Border
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesScrollBorderColor || '#e5e7eb'}
                                        onChange={(value) => handleThemeChange('chatSourcesScrollBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Widget Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTextColor || '#374151'}
                                        onChange={(value) => handleThemeChange('chatSourcesTextColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}


                        {/* E-commerce Results Overlay Customization */}
                        {(theme.interfaceVariant || 'current') === 'ecommerce' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                🎨 Results Overlay
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                Customize the full-page overlay that displays product search results
                            </p>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Background Color</label>
                                    <ColorPicker
                                        value={theme.ecommerceResultsOverlay?.bgColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.bgColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Text Color</label>
                                    <ColorPicker
                                        value={theme.ecommerceResultsOverlay?.textColor || '#1f2937'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.textColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Close Button Background</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.closeButtonBg || 'rgba(255, 255, 255, 0.1)'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.closeButtonBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Close Button Color</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.closeButtonColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.closeButtonColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Product Card Background</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.cardBg || '#ffffff'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.cardBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Product Card Border</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.cardBorderColor || '#e5e7eb'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.cardBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Price Color</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.priceColor || theme.primaryColor || '#007bff'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.priceColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>Loading Indicator Color</label>
                                    <ColorPicker
                                        value={(theme.ecommerceResultsOverlay as any)?.loadingColor || theme.primaryColor || '#007bff'}
                                        onChange={(value) => handleThemeChange('ecommerceResultsOverlay.loadingColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Sources Tab Content (hide for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                📑 Sources Tab
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                Customize the Sources tab when users click to view all sources
                            </p>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tab Border Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTabBorderColor || '#e5e7eb'}
                                        onChange={(value) => handleThemeChange('chatSourcesTabBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tab Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTabTextColor || '#374151'}
                                        onChange={(value) => handleThemeChange('chatSourcesTabTextColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Active Tab Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTabActiveTextColor || '#3b82f6'}
                                        onChange={(value) => handleThemeChange('chatSourcesTabActiveTextColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Source Item Border
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesItemBorderColor || '#e2e8f0'}
                                        onChange={(value) => handleThemeChange('chatSourcesItemBorderColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Source Item Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesItemBg || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatSourcesItemBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Source Header Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesItemHeaderBg || '#f3f4f6'}
                                        onChange={(value) => handleThemeChange('chatSourcesItemHeaderBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Response Marker Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesMarkerBg || '#3b82f6'}
                                        onChange={(value) => handleThemeChange('chatSourcesMarkerBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Response Marker Text
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesMarkerColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatSourcesMarkerColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Sources Tab Marker Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTabMarkerBg || '#10b981'}
                                        onChange={(value) => handleThemeChange('chatSourcesTabMarkerBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Sources Tab Marker Text
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesTabMarkerColor || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatSourcesTabMarkerColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Source Link Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatSourcesLinkColor || '#60a5fa'}
                                        onChange={(value) => handleThemeChange('chatSourcesLinkColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Links & Buttons (hide for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                🔗 Links & Buttons
                            </h4>
                            <p style={{fontSize: 12, color: '#6b7280', marginBottom: 16}}>
                                Customize hyperlink appearance, underline settings, and button colors
                            </p>
                            
                            {/* Link Appearance */}
                            <div style={{marginBottom: 24}}>
                                <h5 style={{fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5563'}}>
                                    Link Appearance
                                </h5>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            Message Hyperlink Color
                                        </label>
                                        <ColorPicker
                                            value={theme.chatHyperlinkColor || '#667eea'}
                                            onChange={(value) => handleThemeChange('chatHyperlinkColor', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            Link Font Weight
                                        </label>
                                        <select
                                            value={theme.chatLinkFontWeight || 'normal'}
                                            onChange={(e) => handleThemeChange('chatLinkFontWeight', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 6,
                                                fontSize: 14,
                                                background: 'white'
                                            }}
                                        >
                                            <option value="normal">Normal</option>
                                            <option value="500">Medium (500)</option>
                                            <option value="600">Semi Bold (600)</option>
                                            <option value="700">Bold (700)</option>
                                            <option value="800">Extra Bold (800)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Link Underline Settings */}
                            <div style={{marginBottom: 24}}>
                                <h5 style={{fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5563'}}>
                                    Link Underline Settings
                                </h5>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 16}}>
                                    <div>
                                        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={theme.chatLinkUnderline === 'true'}
                                                onChange={(e) => handleThemeChange('chatLinkUnderline', String(e.target.checked))}
                                                style={{width: 16, height: 16}}
                                            />
                                            <span style={{fontSize: 14, color: '#374151'}}>Show underline on links</span>
                                        </label>
                                    </div>
                                    <div>
                                        <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                            <input
                                                type="checkbox"
                                                checked={theme.chatLinkHoverUnderline === 'true'}
                                                onChange={(e) => handleThemeChange('chatLinkHoverUnderline', String(e.target.checked))}
                                                style={{width: 16, height: 16}}
                                            />
                                            <span style={{fontSize: 14, color: '#374151'}}>Show underline on hover</span>
                                        </label>
                                    </div>
                                </div>
                                
                                {(theme.chatLinkUnderline === 'true' || theme.chatLinkHoverUnderline === 'true') && (
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Underline Style
                                            </label>
                                            <select
                                                value={theme.chatLinkUnderlineStyle || 'solid'}
                                                onChange={(e) => handleThemeChange('chatLinkUnderlineStyle', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    fontSize: 14,
                                                    background: 'white'
                                                }}
                                            >
                                                <option value="solid">Solid</option>
                                                <option value="dashed">Dashed</option>
                                                <option value="dotted">Dotted</option>
                                                <option value="double">Double</option>
                                                <option value="wavy">Wavy</option>
                                            </select>
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Underline Color
                                            </label>
                                            <select
                                                value={theme.chatLinkUnderlineColor || 'currentColor'}
                                                onChange={(e) => handleThemeChange('chatLinkUnderlineColor', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    fontSize: 14,
                                                    background: 'white'
                                                }}
                                            >
                                                <option value="currentColor">Same as link color</option>
                                                <option value="custom">Custom color</option>
                                            </select>
                                        </div>
                                        {theme.chatLinkUnderlineColor === 'custom' && (
                                            <div className="control-group">
                                                <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                    Custom Underline Color
                                                </label>
                                                <ColorPicker
                                                    value={theme.chatLinkUnderlineCustomColor || '#3b82f6'}
                                                    onChange={(value) => handleThemeChange('chatLinkUnderlineCustomColor', value)}
                                                />
                                            </div>
                                        )}
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Underline Thickness
                                            </label>
                                            <select
                                                value={theme.chatLinkUnderlineThickness || '1px'}
                                                onChange={(e) => handleThemeChange('chatLinkUnderlineThickness', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    fontSize: 14,
                                                    background: 'white'
                                                }}
                                            >
                                                <option value="1px">Thin (1px)</option>
                                                <option value="2px">Medium (2px)</option>
                                                <option value="3px">Thick (3px)</option>
                                                <option value="4px">Extra Thick (4px)</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* View Source Button */}
                            <div>
                                <h5 style={{fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#4b5563'}}>
                                    View Source Button
                                </h5>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            View Source Background
                                        </label>
                                        <ColorPicker
                                            value={theme.chatViewSourceBg || 'rgba(59, 130, 246, 0.1)'}
                                            onChange={(value) => handleThemeChange('chatViewSourceBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            View Source Text
                                        </label>
                                        <ColorPicker
                                            value={theme.chatViewSourceColor || '#60a5fa'}
                                            onChange={(value) => handleThemeChange('chatViewSourceColor', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            View Source Hover Background
                                        </label>
                                        <ColorPicker
                                            value={theme.chatViewSourceHoverBg || 'rgba(59, 130, 246, 0.2)'}
                                            onChange={(value) => handleThemeChange('chatViewSourceHoverBg', value)}
                                        />
                                    </div>
                                    <div className="control-group">
                                        <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                            View Source Hover Text
                                        </label>
                                        <ColorPicker
                                            value={theme.chatViewSourceHoverColor || '#93c5fd'}
                                            onChange={(value) => handleThemeChange('chatViewSourceHoverColor', value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Citation Tooltip Styling (hide for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#374151'}}>
                                📑 Citation Tooltip Styling
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tooltip Background
                                    </label>
                                    <ColorPicker
                                        value={theme.chatCitationTooltipBg || '#1e293b'}
                                        onChange={(value) => handleThemeChange('chatCitationTooltipBg', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Tooltip Text Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatCitationTooltipText || '#ffffff'}
                                        onChange={(value) => handleThemeChange('chatCitationTooltipText', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Citation Tooltip Styling duplicate block removed for Standard and Current */}

                        {/* Clear Conversation Button */}
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                🗑️ Clear Conversation History Button
                            </h4>
                            <div style={{marginBottom: 16}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
                                    <input
                                        type="checkbox"
                                        checked={theme.chatClearButtonShow === 'true'}
                                        onChange={(e) => handleThemeChange('chatClearButtonShow', String(e.target.checked))}
                                        style={{width: 16, height: 16}}
                                    />
                                    <span style={{fontSize: 14, color: '#374151'}}>Show clear conversation button</span>
                                </label>
                            </div>
                            
                            {theme.chatClearButtonShow === 'true' && (
                                <>
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16}}>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Button Text
                                            </label>
                                            <input
                                                type="text"
                                                value={theme.chatClearButtonText || 'Clear Chat'}
                                                onChange={(e) => handleThemeChange('chatClearButtonText', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    fontSize: 14
                                                }}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Position
                                            </label>
                                            <select
                                                value={theme.chatClearButtonPosition || 'header-right'}
                                                onChange={(e) => handleThemeChange('chatClearButtonPosition', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 6,
                                                    fontSize: 14,
                                                    background: 'white'
                                                }}
                                            >
                                                <option value="header-right">Header Right</option>
                                                <option value="header-left">Header Left</option>
                                                <option value="footer">Footer</option>
                                                <option value="input-area">Input Area</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16}}>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Background Color
                                            </label>
                                            <ColorPicker
                                                value={theme.chatClearButtonBg || '#ef4444'}
                                                onChange={(value) => handleThemeChange('chatClearButtonBg', value)}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Text Color
                                            </label>
                                            <ColorPicker
                                                value={theme.chatClearButtonColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('chatClearButtonColor', value)}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Hover Background
                                            </label>
                                            <ColorPicker
                                                value={theme.chatClearButtonHoverBg || '#dc2626'}
                                                onChange={(value) => handleThemeChange('chatClearButtonHoverBg', value)}
                                            />
                                        </div>
                                        <div className="control-group">
                                            <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                                Hover Text Color
                                            </label>
                                            <ColorPicker
                                                value={theme.chatClearButtonHoverColor || '#ffffff'}
                                                onChange={(value) => handleThemeChange('chatClearButtonHoverColor', value)}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Font Size Settings */}
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            marginBottom: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                📏 Font Size Settings
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Base Font Size (px)
                                    </label>
                                    <input
                                        type="number"
                                        min="14"
                                        max="24"
                                        value={theme.chatFontSizeBase || 14}
                                        onChange={(e) => handleThemeChange('chatFontSizeBase', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14
                                        }}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Response Font Size (px)
                                    </label>
                                    <input
                                        type="number"
                                        min="14"
                                        max="24"
                                        value={theme.chatFontSizeResponse || 14}
                                        onChange={(e) => handleThemeChange('chatFontSizeResponse', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14
                                        }}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        User Message Font Size (px)
                                    </label>
                                    <input
                                        type="number"
                                        min="14"
                                        max="24"
                                        value={theme.chatFontSizeUser || 14}
                                        onChange={(e) => handleThemeChange('chatFontSizeUser', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14
                                        }}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Header Font Size (px)
                                    </label>
                                    <input
                                        type="number"
                                        min="12"
                                        max="28"
                                        value={theme.chatFontSizeHeader || 16}
                                        onChange={(e) => handleThemeChange('chatFontSizeHeader', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14
                                        }}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Placeholder Font Size (px)
                                    </label>
                                    <input
                                        type="number"
                                        min="14"
                                        max="20"
                                        value={theme.chatFontSizePlaceholder || 14}
                                        onChange={(e) => handleThemeChange('chatFontSizePlaceholder', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 6,
                                            fontSize: 14
                                        }}
                                    />
                                </div>
                            </div>
                        </div>


                        {/* Core Theme Settings (hide for Standard) */}
                        {(theme.interfaceVariant || 'current') === 'current' && (
                        <div style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: 24,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            <h4 style={{fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#374151'}}>
                                🎯 Core Theme Settings
                            </h4>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16}}>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Chat Background Color
                                    </label>
                                    <ColorPicker
                                        value={theme.chatBgColor}
                                        onChange={(value) => handleThemeChange('chatBgColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Primary Color
                                    </label>
                                    <ColorPicker
                                        value={theme.primaryColor}
                                        onChange={(value) => handleThemeChange('primaryColor', value)}
                                    />
                                </div>
                                <div className="control-group">
                                    <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6}}>
                                        Secondary Color
                                    </label>
                                    <ColorPicker
                                        value={theme.secondaryColor}
                                        onChange={(value) => handleThemeChange('secondaryColor', value)}
                                    />
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                )}
            </div>

            {/* Save Notification */}
            {saveNotification.show && (
                <div className={`save-notification ${saveNotification.type} ${!saveNotification.show ? 'fade-out' : ''}`}>
                    {saveNotification.message}
                </div>
            )}

            {/* Variants Modal */}
            {showVariantModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]" style={{ zIndex: 9999 }}>
                    <div className={`max-w-6xl w-full rounded-2xl shadow-2xl overflow-hidden ${
                        isDarkMode ? 'bg-gray-800' : 'bg-white'
                    }`} style={{ maxHeight: '90vh', overflow: 'auto' }}>
                        {/* Modal Header */}
                        <div className={`px-6 py-4 border-b ${
                            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                        }`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className={`text-xl font-bold ${
                                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                                    }`}>
                                        🎨 Choose Theme Variant
                                    </h2>
                                    <p className={`mt-2 ${
                                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {isLoadingVariants
                                            ? 'Loading beautiful theme variants based on your brand colors...'
                                            : 'Select a theme variant that best matches your brand. Changes apply instantly to the preview.'
                                        }
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowVariantModal(false)}
                                    className={`p-2 rounded-full hover:bg-gray-100 ${
                                        isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            {isLoadingVariants ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                        Generating theme variants based on your brand...
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-6">
                                    {themeVariants.map((variant, index) => (
                                        <div
                                            key={index}
                                            className={`relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                                                selectedVariant === index
                                                    ? 'ring-4 ring-purple-500 transform scale-105'
                                                    : 'hover:transform hover:scale-[1.02]'
                                            } ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                            onClick={() => {
                                                console.log('Theme variant clicked:', index);
                                                applyThemeVariant(index);
                                                setTimeout(() => setShowVariantModal(false), 1000); // Close after 1 second to show success message
                                            }}
                                        >
                                            {/* Selected Badge */}
                                            {selectedVariant === index && (
                                                <div className="absolute top-4 right-4 bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                                                    Selected
                                                </div>
                                            )}

                                            {/* Theme Preview */}
                                            <div className="p-5">
                                                <h3 className={`text-sm font-semibold mb-3 tracking-wide ${
                                                    isDarkMode ? 'text-gray-200' : 'text-gray-900'
                                                }`}>
                                                    {variant.name || `Theme ${index + 1}`}
                                                </h3>

                                                {(() => {
                                                    const mapping = variant.mapping || variant;
                                                    const isEcommerceVariant = (variant.type === 'ecommerce_widget') || !!(mapping.voiceWidgetBgColor || mapping.overlayBgColor || mapping.productCardBg);

                                                    if (isEcommerceVariant) {
                                                        const voiceBg = mapping.voiceWidgetBgColor || mapping.buttonBgColor || '#007bff';
                                                        const voiceIcon = mapping.voiceWidgetIconColor || '#ffffff';
                                                        const overlayBg = mapping.overlayBgColor || '#ffffff';
                                                        const overlayText = mapping.overlayTextColor || '#1f2937';
                                                        const overlayBorder = mapping.overlayBorderColor || 'rgba(0,0,0,0.08)';
                                                        const priceColor = mapping.priceColor || voiceBg;
                                                        const ctaBg = mapping.buttonBgColor || voiceBg;
                                                        const ctaText = mapping.buttonTextColor || '#ffffff';

                                                        return (
                                                            <div className="rounded-xl overflow-hidden shadow-lg" style={{ width: 220, background: overlayBg, border: `1px solid ${overlayBorder}` }}>
                                                                {/* Voice trigger button preview */}
                                                                <div className="p-3 flex items-center justify-between" style={{ background: overlayBg, borderBottom: `1px solid ${overlayBorder}` }}>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: voiceBg, color: voiceIcon }}>
                                                                            🎙️
                                                                        </div>
                                                                        <div className="text-[11px]" style={{ color: overlayText }}>Voice Shopping</div>
                                                                    </div>
                                                                    <div className="text-[10px] opacity-70" style={{ color: overlayText }}>E-commerce</div>
                                                                </div>

                                                                {/* Product overlay preview */}
                                                                <div className="p-3 space-y-2" style={{ background: overlayBg }}>
                                                                    <div className="rounded-md border" style={{ borderColor: overlayBorder, background: mapping.productCardBg || '#ffffff' }}>
                                                                        <div className="w-full h-16 rounded-t-md" style={{ background: mapping.productCardBg || '#f3f4f6', borderBottom: `1px solid ${overlayBorder}` }}></div>
                                                                        <div className="p-2">
                                                                            <div className="text-[11px] font-semibold truncate" style={{ color: overlayText }}>Sample Product</div>
                                                                            <div className="text-[11px]" style={{ color: priceColor }}>$49.00</div>
                                                                            <div className="mt-2 text-[10px] px-2 py-1 rounded" style={{ background: ctaBg, color: ctaText, display: 'inline-block' }}>Add to Cart</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Chat interface preview (default)
                                                    const gradientStart = mapping.gradientStartColor || mapping.chatContainerGradientStart;
                                                    const gradientEnd = mapping.gradientEndColor || mapping.chatContainerGradientEnd;
                                                    const chatBackground = mapping.chatContainerGradient
                                                        || (gradientStart && gradientEnd ? `linear-gradient(140deg, ${gradientStart} 0%, ${gradientEnd} 100%)`
                                                        : mapping.chatContainerBg || '#f4f4f5');
                                                    const headerBackground = mapping.headerBg
                                                        || `linear-gradient(135deg, ${mapping.primaryColor || '#8b5cf6'} 0%, ${mapping.secondaryColor || mapping.primaryColor || '#8b5cf6'} 100%)`;
                                                    const assistantBg = mapping.messageResponseBg || '#ffffff';
                                                    const userBg = mapping.sendButtonBg || mapping.buttonBgColor || mapping.primaryColor || '#8b5cf6';
                                                    const borderColor = mapping.borderColor || 'rgba(0,0,0,0.06)';
                                                    const inputBg = mapping.inputFieldBg || '#ffffff';
                                                    const textColor = mapping.chatTextColor || '#1f2937';
                                                    const secondaryText = mapping.inputTextColor || '#6b7280';

                                                    return (
                                                        <div className="rounded-xl overflow-hidden shadow-lg" style={{ width: 220, background: chatBackground }}>
                                                            <div className="px-3 py-2 flex items-center justify-between" style={{ background: headerBackground, color: mapping.headerTextColor || '#ffffff' }}>
                                                                <div className="flex items-center gap-2 text-xs">
                                                                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{
                                                                        background: mapping.floatingAvatarBgColor || mapping.primaryColor || '#8b5cf6',
                                                                        color: mapping.floatingAvatarIconColor || '#ffffff'
                                                                    }}>
                                                                        AI
                                                                    </div>
                                                                    <span className="font-semibold truncate max-w-[90px]">
                                                                        AI Assistant
                                                                    </span>
                                                                </div>
                                                                <span className="text-[10px] opacity-80">Live</span>
                                                            </div>

                                                            <div className="px-3 py-3 space-y-2" style={{ background: chatBackground }}>
                                                                <div className="max-w-[85%] rounded-lg px-3 py-2 text-[11px] leading-tight" style={{
                                                                    background: assistantBg,
                                                                    color: textColor,
                                                                    border: `1px solid ${borderColor}`
                                                                }}>
                                                                    Hi there! Ask anything about your business.
                                                                </div>
                                                                <div className="ml-auto max-w-[85%] rounded-lg px-3 py-2 text-[11px] leading-tight shadow-sm" style={{
                                                                    background: userBg,
                                                                    color: mapping.sendButtonTextColor || mapping.buttonTextColor || '#ffffff'
                                                                }}>
                                                                    Tell me about your services
                                                                </div>
                                                            </div>

                                                            <div className="px-3 py-2 border-t flex items-center gap-2" style={{ borderColor, background: inputBg }}>
                                                                <div className="flex-1 text:[10px] px-2 py-1.5 rounded border" style={{
                                                                    borderColor,
                                                                    color: secondaryText,
                                                                    background: inputBg
                                                                }}>
                                                                    Type a message...
                                                                </div>
                                                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px]" style={{
                                                                    background: userBg,
                                                                    color: mapping.sendButtonTextColor || mapping.buttonTextColor || '#ffffff'
                                                                }}>
                                                                    ➤
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Description */}
                            <div className={`mt-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <p>These theme variants are generated based on your brand colors extracted from your website.</p>
                                <p className="mt-2">The selected theme will be applied to your chat widget in real-time.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThemeEditor;